import { closeSync, openSync, readFileSync, readSync, statSync, writeSync } from "node:fs";
import * as ast from "../../frontend/ast.js";
import { builtinContainerInherentImpls, builtinEnums, builtinImpls, builtinStructs, builtinTraits, preludeFns } from "../../frontend/stdlib.js";
import { Causality, CausalityTracker, ExtraCauses } from "./causality.js";
import { CollectionIterator, RuntimeIterator } from "./collection-iter.js";
import { Store } from "./store.js";
import { Trace, TraceEvent } from "./trace.js";
import { Transaction } from "./transaction.js";
import {
  DebugFrame,
  DebugHolding,
  DebugSnapshot,
  DebugPrevAttempt,
  DebugThread,
  DebugThreadState,
  DebugWaiting,
} from "./debug.js";
import { planLocks, TxnStrategy } from "../../middle-end/lockplan.js";
import { BinaryResolution, Elaboration, emptyElaboration, IterResolution } from "../../middle-end/elaboration.js";
import { Type } from "../../frontend/types.js";
import {
  Address,
  StoredObject,
  Value,
  VOID,
  bool,
  char,
  charString,
  displayValue,
  float,
  int,
  isNumeric,
  keySegment,
  ptr,
  str,
  truthy,
  valuesEqual,
} from "./value.js";

export class RuntimeError extends Error {
  constructor(message: string, public span?: ast.Span) {
    super(message);
    this.name = "RuntimeError";
  }
}

// ---------------------------------------------------------------------------
// Scheduler effects and completions
// ---------------------------------------------------------------------------

type Effect =
  | { kind: "sleep"; duration: number }
  | { kind: "spawn"; thread: Thread }
  | { kind: "tick" }
  // 2PL lock ops: a lock txn parks until it holds its whole footprint, runs once,
  // then releases. `addrs` is the sorted, deduped footprint; `txn`/`line` tag the
  // trace events the scheduler emits when it grants, parks, or frees the locks.
  | { kind: "acquire"; addrs: Address[]; txn: number; line: number }
  | { kind: "release"; addrs: Address[]; txn: number; line: number }
  // `retry`: the running txn abandoned its attempt and parks until a committed
  // write touches one of `readSet`, then re-runs from the top (see `wakeRetryParked`).
  | { kind: "retry"; readSet: Address[]; txn: number; line: number };

/** Thrown by a `retry` statement to unwind the interpreter's JS call stack back to
 *  the enclosing `execOccTxn`, which parks the thread on its read set. A distinct,
 *  non-user-visible signal — the same "tagged unwinder" `RaiseSignal` reuses. */
class RetrySignal {
  constructor(readonly line: number) {}
}

/** A raised error in flight. Everything but `value` is computed **at the raise
 *  site**, where a generator context is available: the type name it is caught by,
 *  the rendered `message()`, the `from` cause, and the stack trace — which is the
 *  point of exceptions over a returned `Res` (see docs/exceptions.md). */
interface RaisedError {
  value: Value;
  /** The runtime type name a typed `catch` matches, or null for a value whose type
   *  can't be named (a primitive — the checker rejects raising one anyway). */
  typeName: string | null;
  message: string;
  /** The `from` clause's error, with its own trace when it came from a `catch`. */
  cause: RaisedError | null;
  stack: Frame[];
}

/** Thrown by `raise` to unwind to the nearest matching `catch` clause — or, when
 *  nothing catches it, out of the thread entirely, aborting the program with the
 *  message and trace (see `formatUncaught`). The exception analogue of
 *  `RetrySignal`: both are JS-level unwinders the interpreter alone understands. */
class RaiseSignal {
  constructor(readonly error: RaisedError) {}
}

type Completion = { type: "normal" } | { type: "return"; value: Value };

const NORMAL: Completion = { type: "normal" };

type StmtGen = Generator<Effect, Completion, void>;
type ExprGen = Generator<Effect, Value, void>;

/**
 * The one iteration protocol a `for` loop drives. Every iterable source — a
 * built-in collection or a user `Iterator`/`Iterable` — is turned into one of
 * these, so `execFor` has a single loop instead of a branch per source. A
 * built-in collection gets a *native* iterator (its `next` is a TS closure over a
 * materialized snapshot, not an STM method); a user iterator's methods are called
 * through the same interface. This is how the collections stop being iteration
 * "special cases" — and why a range needs no special path either: `0..n` is just a
 * stdlib `Range`, an ordinary user `Iterable` driven like any other.
 *
 * The type itself lives in collection-iter.ts (alongside the stdlib iterators)
 * and is re-exported from there.
 */

// ---------------------------------------------------------------------------
// Lexical scopes (variables only; functions/types are resolved globally)
// ---------------------------------------------------------------------------

class Scope {
  private vars = new Map<string, Value>();
  constructor(private parent: Scope | null = null) {}

  has(name: string): boolean {
    return this.vars.has(name) || (this.parent?.has(name) ?? false);
  }
  get(name: string): Value | undefined {
    return this.vars.get(name) ?? this.parent?.get(name);
  }
  declare(name: string, value: Value): void {
    this.vars.set(name, value);
  }
  /** Assign to the nearest scope that defines `name`; returns false if none. */
  assign(name: string, value: Value): boolean {
    if (this.vars.has(name)) {
      this.vars.set(name, value);
      return true;
    }
    return this.parent?.assign(name, value) ?? false;
  }
  /** This scope's own bindings (not the parent's), for the debugger's locals view. */
  ownEntries(): [string, Value][] {
    return [...this.vars];
  }
  /** The enclosing scope, or null at a frame root. */
  get enclosing(): Scope | null {
    return this.parent;
  }
}

// ---------------------------------------------------------------------------
// Call frames (reified only so the debugger can read them; the interpreter's
// own control flow still rides the JS generator stack)
// ---------------------------------------------------------------------------

/**
 * One activation record on a thread's call stack. The interpreter doesn't need
 * these to run — execution recurses through generators — but two features read
 * them: a `raise` snapshots them as the error's stack trace, and the playground
 * debugger shows them. Pushed/popped per function/method/closure/thread-body call
 * (see `pushFrame`), with each frame's `line` tracking the statement running in it.
 *
 * `rootScope`/`scope` are for the debugger's locals view: `scope` tracks the
 * innermost block currently executing within the frame, and `rootScope` marks
 * where the frame's lexical chain begins, so reading its locals stops at the call
 * boundary. A stack-trace snapshot drops them (see `captureStack`).
 */
interface Frame {
  /** Display label / function name: a plain `fn`, `Type.method`, `<closure>`, or the thread fn. */
  fn: string;
  /** 1-based line the frame is currently at. */
  line: number;
  /** Source file the line indexes into (undefined = user program). */
  source?: string;
  /** Debugger only: the frame's base scope (params / receiver), the locals-walk boundary. */
  rootScope?: Scope;
  /** Debugger only: the innermost scope currently executing (a nested block). */
  scope?: Scope;
}

// ---------------------------------------------------------------------------
// Threads
// ---------------------------------------------------------------------------

class Thread {
  currentTxn: Transaction | null = null;
  /** Under 2PL: the id of the lock txn this thread is running, whenever
   *  `currentTxn` isn't set to carry it — the acquire→begin window and the
   *  commit→release tail. Lets a snapshot attribute the held footprint to its txn
   *  across the whole lock lifetime, not just while the body runs. */
  pendingTxn: number | null = null;
  /** The attempt this thread just abandoned — `retry`-parked or aborted; one
   *  concept, see `DebugPrevAttempt`. Set the moment the attempt gives up, *before*
   *  the `txn-retry` / `txn-abort` event is emitted so that row's snapshot already
   *  shows it, and cleared once the replacement's `txn-begin` has been emitted. For
   *  a park it spans the whole blocked stretch (the scheduler's `retryParked` entry
   *  covers a subset of it); for an abort, only the immediately following re-begin. */
  prevAttempt: DebugPrevAttempt | null = null;
  time = 0;
  /** 1-based source line of the statement currently executing (for traces). */
  currentLine = 0;
  /** This thread's call stack, outermost first — snapshotted by `raise` as the
   *  error's stack trace, and read by the debugger. Pushed/popped around every
   *  function/method/closure/thread-body call; each frame's `line` tracks the
   *  statement running in it. */
  readonly frames: Frame[] = [];
  /** The errors whose `catch` handlers are currently running, innermost last. `raise
   *  x from e` consults it to recover the caught error's own trace for the chain. */
  caught: RaisedError[] = [];
  gen!: Generator<Effect, void, void>;
  constructor(readonly id: number, readonly fnName: string) {}
}

interface ReadyEntry {
  thread: Thread;
  time: number;
  seq: number;
  /** Seeded tie-break among threads runnable at the same instant — a hash of
   *  `(seed, seq)`, so it is deterministic for a seed but bears no relation to
   *  the order threads became runnable. Derived per entry rather than drawn from
   *  a running PRNG on purpose: overruling one scheduling decision must not
   *  perturb the priorities of entries already in the queue, which is what lets
   *  a dragged trace event move without reshuffling the whole tail. */
  priority: number;
  /** The step during whose handling `enqueue` pushed this entry — what made the
   *  thread runnable. Recorded because it is exactly the relation that
   *  constrains reordering (a step can never run before the step that enabled
   *  it), so the playground gets it without reconstructing a happens-before
   *  graph. -1 for main's initial enqueue. */
  enabledBy: number;
}

/**
 * Overrule the scheduler's `seq` tie-break for a window of steps: at step
 * `from + i`, run thread `threads[i]`. This is how the playground realizes a
 * trace event the user dragged past another (see
 * docs/design/trace-reordering.md) — the prefix before `from` needs no forcing,
 * since a run is deterministic for a given seed and replays identically.
 *
 * A pick is honored only if that thread is runnable *and* sits at the minimum
 * ready time; virtual time is the model of time, not a scheduling choice, so it
 * is never negotiable. A pick that isn't available abandons the override (the
 * default heuristic finishes the run) and sets `RunResult.scheduleAbandoned`.
 * The scheduler can be overruled, but not lied to.
 */
/** Shared empty array for runs with causality off, so a plain run allocates
 *  nothing per step. */
const EMPTY_CHOICES: number[] = [];

export interface ScheduleOverride {
  /** Step index the forced window starts at. */
  from: number;
  /** Thread to run at each step of the window, in order. */
  threads: number[];
}

// ---------------------------------------------------------------------------
// Interpreter options / result
// ---------------------------------------------------------------------------

export interface RunOptions {
  /** Makes a run reproducible, and — since it drives the scheduler's tie-break
   *  among threads runnable at the same instant — selects *which* interleaving
   *  you get. Different seeds are different legal schedules of the same program.
   *  Defaults to 42. */
  seed?: number;
  maxSteps?: number;
  maxTime?: number;
  /** Also echo `print` output to the console. */
  echo?: boolean;
  /** The program's command-line arguments (what `args()`/`numArgs()`/`argAt()`
   *  read), *not* including the program name. Defaults to none. */
  args?: string[];
  /**
   * How to run `txn` blocks:
   *   "occ"    — every txn optimistic (snapshot + validate + retry). The default.
   *   "hybrid" — a txn whose footprint is statically known (per the LockPlan) is
   *              run pessimistically instead: lock the footprint, run once, no
   *              retries. Emulates what the Go backend's 2PL path does.
   */
  strategy?: "occ" | "hybrid";
  /** Record a debugger snapshot (all threads' call stacks, locals, and txn
   *  read/write sets) alongside every trace event. Off by default — it adds
   *  per-event capture overhead — but the playground enables it to drive its
   *  VSCode-style debugger. */
  debug?: boolean;
  /** Record causal event/step ids alongside the trace (`RunResult.causality`).
   *  Off by default; the playground enables it to name the events a user drags
   *  and the scheduling decisions a reordering refers to. */
  causality?: boolean;
  /** Overrule the scheduler's tie-break for a window of steps. */
  schedule?: ScheduleOverride;
}

export interface RunResult {
  trace: Trace;
  store: Store;
  steps: number;
  finalTime: number;
  aborts: number;
  commits: number;
  /** One debugger snapshot per trace event (`debug[i]` describes the world when
   *  `trace.events[i]` was produced), or null when `RunOptions.debug` was off. */
  debug: DebugSnapshot[] | null;
  /** Causal event/step ids, or null when `RunOptions.causality` was off. Kept
   *  beside the trace rather than inside it, so the trace format, `--json`, and
   *  the golden files are untouched by it. */
  causality: Causality | null;
  /** True when a `RunOptions.schedule` override asked for a thread that wasn't
   *  available, so the rest of the run fell back to the default heuristic.
   *  False when no override was requested. */
  scheduleAbandoned: boolean;
}

// ---------------------------------------------------------------------------
// Interpreter
// ---------------------------------------------------------------------------

export class Interpreter {
  private structs = new Map<string, ast.StructDecl>();
  /** Struct names that are value types (every field `key`; see `isValueType`). */
  private valueTypes = new Set<string>();
  private enums = new Map<string, ast.EnumDecl>();
  private variantIndex = new Map<string, { enumName: string; variant: ast.EnumVariant }>();
  private fns = new Map<string, ast.FnDecl>();
  /** User-defined methods from `impl` blocks: type name -> method name -> decl.
   *  Dispatched dynamically by a receiver's runtime type. */
  private methods = new Map<string, Map<string, ast.FnDecl>>();
  /** Whether any type implements `show` (the `Show` trait) — gates `print`/`str`'s
   *  Show dispatch so programs without it keep the exact default rendering path. */
  private hasShow = false;
  /** Every method name any type implements. Gates operator/`Show` dispatch so a
   *  program that overloads nothing keeps the exact built-in paths (and never
   *  reads an operand's marker to check for an overload it doesn't have). */
  private definedMethodNames = new Set<string>();

  private store = new Store();
  private trace = new Trace();

  private ready: ReadyEntry[] = [];
  private seq = 0;
  private nextThreadId = 0;
  private nextTxnId = 1;

  /** Whether to record debugger snapshots (see `RunOptions.debug`). */
  private readonly debugEnabled: boolean;
  /** One snapshot per trace event, appended in `emit`. Empty unless `debugEnabled`. */
  private readonly snapshots: DebugSnapshot[] = [];
  /** Every thread that has started and not yet ended — the set a snapshot walks.
   *  A thread joins in `runThread` (before its `thread-start` event) and leaves
   *  after its `thread-end`, so both bracket events see it. */
  private readonly liveThreads = new Map<number, Thread>();

  /** Causal event/step ids, or null when `RunOptions.causality` is off — so a
   *  plain run pays nothing for them. */
  private readonly causal: CausalityTracker | null;
  /** The user's tie-break override, and whether it is still in force. */
  private readonly schedule?: ScheduleOverride;
  private scheduleAbandoned = false;
  /** Index of the step currently running (-1 before the loop starts). Read by
   *  `enqueue` (to stamp `enabledBy`) and by the schedule override. */
  private stepIndex = -1;
  /** The virtual time the step currently running is at. A ready entry stamped
   *  later than this is a sleeper rather than a runnable thread, which is what
   *  lets a snapshot tell the two apart (see `threadState`). */
  private now = 0;

  /** Per-`txn` strategies under "hybrid"; undefined means every txn is OCC. */
  private strategies?: Map<ast.TxnStmt, TxnStrategy>;
  /** Held 2PL locks: address → owning thread id. Empty unless "hybrid". */
  private locks = new Map<Address, number>();
  /** Threads blocked waiting to acquire a footprint (see `wakeParked`). */
  private parked: { thread: Thread; addrs: Address[]; txn: number; line: number }[] = [];
  /** Threads blocked in `retry`, each tagged with the read set it re-runs on. A
   *  committed write overlapping that set wakes the thread (see `wakeRetryParked`). */
  private retryParked: { thread: Thread; readSet: Set<Address>; txn: number; line: number }[] = [];
  private rngState: number;
  /** Seed for the scheduler's tie-break (see `priorityFor`). Separate from
   *  `rngState`, which is the `random()` builtin's stream: a program drawing
   *  random numbers must not shift the schedule, and vice versa. */
  private readonly schedSeed: number;
  private steps = 0;
  private aborts = 0;
  private commits = 0;

  private readonly maxSteps: number;
  private readonly maxTime: number;
  private readonly echo: boolean;
  /** The program's command-line arguments, read by `_argCount`/`_argAt` (and thus
   *  the `args()` prelude function). Empty unless the CLI passes some. */
  private readonly cliArgs: string[];
  /** A snapshot of the host environment as `NAME=value` strings, read by
   *  `_envCount`/`_envEntry` (and thus `envVars()`). Snapshotted once so the count
   *  and the per-index reads a single `envVars()` call makes stay consistent.
   *  `null` when there is no host process to read (the browser playground has no
   *  `process` global) — the env builtins then error out, like file IO does. */
  private readonly envSnapshot: string[] | null;
  private readonly stdlibRangeActive: boolean;

  /** The checker's per-node decisions (iteration plans, call targets, operator
   *  overloads) — the same IR the Go backend lowers. The interpreter reads it to
   *  classify operations rather than re-deriving them (e.g. iterability by trait,
   *  not by a method named `next`). Empty when constructed without one. */
  private elab: Elaboration;

  constructor(program: ast.Program, opts: RunOptions = {}, elab: Elaboration = emptyElaboration()) {
    this.elab = elab;
    this.rngState = (opts.seed ?? 42) >>> 0;
    this.schedSeed = (opts.seed ?? 42) >>> 0;
    this.maxSteps = opts.maxSteps ?? 200_000;
    this.maxTime = opts.maxTime ?? Infinity;
    this.echo = opts.echo ?? false;
    this.debugEnabled = opts.debug ?? false;
    this.causal = opts.causality ? new CausalityTracker() : null;
    this.schedule = opts.schedule;
    this.cliArgs = opts.args ?? [];
    // `process` is a Node global, absent in the browser playground bundle. Guard
    // the read so constructing an interpreter never throws there; the env builtins
    // check for the resulting `null` and error out on use, like file IO does.
    this.envSnapshot =
      typeof process === "undefined"
        ? null
        : Object.entries(process.env)
            .filter(([, v]) => v !== undefined)
            .map(([k, v]) => `${k}=${v}`);

    // Built-in prelude (`Pair`, the `Option`/`Res` enums, the STM-written
    // free functions like `min`/`max`) plus the user's declarations; a program may
    // shadow a builtin by declaring the same name.
    // A `builtin struct` (a container like `Dict`) is a checker/tooling declaration
    // only — the interpreter backs collections natively (via `isGenericContainer` +
    // the runtime collection Value), so it's not registered as a real struct here.
    const builtins = [...builtinStructs().filter((s) => !s.builtin), ...builtinEnums(), ...preludeFns()].filter(
      (e) => !ast.shadowsPrelude(program, e.name)
    );
    // The trait table must include the built-in traits (a program may shadow one),
    // so an impl inherits their default methods — e.g. `Iterable.map` / `.reduce`.
    const traits = new Map<string, ast.TraitDecl>();
    for (const t of builtinTraits()) traits.set(t.name, t);
    for (const d of program.decls) if (d.kind === "trait") traits.set(d.name, d);
    // Built-in impls (the iterator adapters behind `map`, the `Range` iterable) are
    // registered like any other, after the user's so a user impl on the same type
    // would win. Drop any built-in impl whose target type the program shadows with
    // its own declaration (mirrors the built-in struct/enum filter above).
    const shadowedTypes = new Set(
      program.decls.filter((d) => d.kind === "struct" || d.kind === "enum").map((d) => d.name)
    );
    // The stdlib `Range` is in effect (not shadowed by a user type of the same name),
    // so a `Range` value has the known start..end-1/+1 semantics the `for`-loop
    // peephole (`asStdlibRange`) relies on to skip the `RangeIter` cursor.
    this.stdlibRangeActive = !shadowedTypes.has("Range") && !shadowedTypes.has("RangeIter");
    const builtinI = builtinImpls().filter((im) => !shadowedTypes.has(im.typeName));
    // A container's inherent impl also carries STM-bodied derived methods
    // (`Set.union`/`intersection`/`difference`) — kept out of `builtinImpls` since its
    // other inherent ops are native. Register only the bodied ones so they dispatch as
    // ordinary user methods (via `invokeUserMethod`); the native flat ops still win in
    // `dispatchMethod` (see `isBuiltinContainerMethod`), so this can't shadow them.
    const containerDerived = builtinContainerInherentImpls()
      .filter((im) => !shadowedTypes.has(im.typeName))
      .map((im) => ({ ...im, methods: im.methods.filter((m) => !m.builtin) }))
      .filter((im) => im.methods.length > 0);
    for (const decl of [...builtins, ...builtinI, ...containerDerived, ...program.decls]) {
      switch (decl.kind) {
        case "struct":
          this.structs.set(decl.name, decl);
          if (ast.isValueType(decl)) this.valueTypes.add(decl.name); // never a generic struct
          break;
        case "enum":
          this.enums.set(decl.name, decl);
          for (const v of decl.variants) {
            if (!this.variantIndex.has(v.name)) {
              this.variantIndex.set(v.name, { enumName: decl.name, variant: v });
            }
          }
          break;
        case "fn":
          this.fns.set(decl.name, decl);
          break;
        case "impl": {
          // Seed the target with omitted defaults, then let explicit impl methods
          // override them. Dispatch remains based on the receiver's runtime type.
          let table = this.methods.get(decl.typeName);
          if (!table) this.methods.set(decl.typeName, (table = new Map()));
          const overridden = new Set(decl.methods.map((m) => m.name));
          for (const m of traits.get(decl.trait)?.methods ?? []) {
            if (m.body && !overridden.has(m.name)) {
              table.set(m.name, { ...m, kind: "fn", body: m.body });
            }
          }
          for (const m of decl.methods) table.set(m.name, m);
          break;
        }
        // Trait defaults are installed when their impl is registered.
      }
    }
    for (const table of this.methods.values()) for (const name of table.keys()) this.definedMethodNames.add(name);
    this.hasShow = this.definedMethodNames.has("show");

    // Under "hybrid", precompute where the locks go once, up front (the same
    // decision the Go backend consumes). "occ" leaves `strategies` undefined, so
    // every txn takes the optimistic path and no lock code ever runs.
    if (opts.strategy === "hybrid") {
      this.strategies = planLocks(program, { strategy: "hybrid", elaboration: this.elab }).strategies;
    }
  }

  // -- public entry -------------------------------------------------------

  run(entry = "main"): RunResult {
    const mainFn = this.fns.get(entry);
    if (!mainFn) throw new RuntimeError(`no function \`${entry}\` to run`);

    const main = new Thread(this.nextThreadId++, entry);
    main.gen = this.runThread(mainFn, [], main);
    this.enqueue(main, 0);

    while (this.ready.length > 0) {
      this.stepIndex++;
      const idx = this.pickNext();
      // Before the pick leaves the queue: who else could have gone here. This is
      // the branching of the execution space, and the only thing a different
      // schedule can differ in — see `RunOptions.causality` and the timeline tree
      // in the playground.
      const choices = this.causal ? this.readyChoices() : EMPTY_CHOICES;
      const entryItem = this.ready.splice(idx, 1)[0]!;
      const thread = entryItem.thread;
      if (entryItem.time > this.maxTime) break; // virtual-time budget exhausted
      thread.time = entryItem.time;
      this.now = entryItem.time;

      if (++this.steps > this.maxSteps) {
        throw new RuntimeError(
          `step limit exceeded (${this.maxSteps}); the program may not terminate`
        );
      }

      // A step is one `gen.next()` on one thread: everything it does between two
      // consecutive pauses — zero or more trace events, ended by the effect it
      // yields (or by the thread's body returning). A step is the atom a
      // reordering moves, since the events inside one had no interleaving point
      // between them. It is bounded by yields, not by events, so a step that
      // emits nothing is ordinary: the thread resumed and reached the next
      // interleaving point without recording anything. See
      // docs/concurrency.md#steps-ticks-and-rows.
      this.causal?.beginStep(
        thread.id,
        entryItem.time,
        entryItem.enabledBy,
        this.trace.events.length,
        choices
      );
      try {
        let step: IteratorResult<Effect, void>;
        try {
          step = thread.gen.next();
        } catch (e) {
          // An exception nothing caught has unwound out of a whole thread. There is no
          // frame left that could handle it, so the program aborts with the error, its
          // stack trace, and the chain of causes — on `main` and on a `go`-spawned
          // thread alike (a fire-and-forget thread failing silently would be worse).
          if (e instanceof RaiseSignal) throw new RuntimeError(formatUncaught(e.error, thread));
          throw e;
        }
        const { value: effect, done } = step;
        if (done) {
          continue; // the thread's body finished (fire-and-forget)
        }
        this.handleEffect(thread, effect!);
      } finally {
        this.causal?.endStep(thread.id, this.trace.events.length);
      }
    }

    // A natural drain (ready queue empty) with threads still blocked means they can
    // never make progress — a deadlock. A lock txn never sleeps, so a thread holding
    // locks is always runnable; a thread blocked in `retry` needs a committed write
    // that overlaps its read set, which will never come. A `--max-time`/`--max-steps`
    // break leaves the queue non-empty, so it isn't mistaken for a deadlock.
    if (this.ready.length === 0) {
      if (this.parked.length > 0) {
        throw new RuntimeError("deadlock: threads parked on locks with nothing runnable");
      }
      if (this.retryParked.length > 0) {
        throw new RuntimeError("deadlock: threads blocked in `retry` with nothing runnable");
      }
    }

    return {
      trace: this.trace,
      store: this.store,
      steps: this.steps,
      finalTime: this.lastTime,
      aborts: this.aborts,
      commits: this.commits,
      debug: this.debugEnabled ? this.snapshots : null,
      causality: this.causal ? this.causal.result() : null,
      scheduleAbandoned: this.scheduleAbandoned,
    };
  }

  private lastTime = 0;

  // -- debugger: trace emission + per-event snapshots ---------------------

  /** Record a trace event and, when debugging, capture a snapshot of the world
   *  as it stands right now (the acting `thread` at the point the event fired).
   *  Every `trace.record` in the interpreter goes through here, so `snapshots`
   *  stays index-aligned with `trace.events`.
   *
   *  **Emit *after* the transition the event describes.** The snapshot is taken
   *  here, so the world must already be in the state the event announces: park
   *  before emitting `lock-wait`, roll back before emitting `orelse-retry`, and so
   *  on. Emitting first records the *previous* state against the new event, which
   *  silently makes the debugger and graph view contradict the row they belong to
   *  (the trace's own text columns still look right, so it hides well). If the
   *  state isn't reachable from the live objects at that point, carry it — see
   *  `Thread.prevAttempt` / `Thread.pendingTxn`. Background and examples:
   *  docs/trace-snapshots.md.
   *
   *  `causes` carries the causal predecessors the event itself doesn't spell out
   *  (the release a lock handoff came from, the commit that invalidated a read
   *  set); the rest — program order, thread creation, reads-from, retry wakes —
   *  the tracker derives on its own. Returns the event's index, which is what a
   *  later event names it by. */
  private emit(ev: TraceEvent, thread: Thread, causes?: ExtraCauses): number {
    const index = this.trace.events.length;
    this.trace.record(ev);
    this.causal?.recordEvent(index, ev, causes);
    if (this.debugEnabled) this.snapshots.push(this.captureSnapshot(thread.id));
    return index;
  }

  /** Push a call frame whose lexical chain starts at `rootScope`. Frames are
   *  always maintained (a `raise` snapshots them as its stack trace); the
   *  `rootScope`/`scope` refs additionally power the debugger's locals view. `loc`
   *  is the frame's own declaration span. */
  private pushFrame(thread: Thread, fn: string, rootScope: Scope, loc: ast.Span): void {
    thread.frames.push({ fn, rootScope, scope: rootScope, line: loc.start.line, source: loc.source });
  }

  private popFrame(thread: Thread): void {
    thread.frames.pop();
  }

  /** A located span carries a real position (offset > 0). Stdlib declarations now
   *  keep their real spans, so this is true for both user and prelude code; it
   *  still guards the rare synthetic offset-0 span from moving a frame's line. */
  private isLocated(span: ast.Span): boolean {
    return span.start.offset !== 0 || span.source === undefined;
  }

  private captureSnapshot(actingThread: number): DebugSnapshot {
    // What each currently-blocked thread is waiting on. A parked thread has no
    // live `currentTxn` (a `retry` abandons its attempt before parking), so this
    // is the only place its waited-on read set / lock footprint survives.
    // Threads blocked acquiring a 2PL footprint. (A `retry` park needs no entry
    // here: `Thread.prevAttempt` already carries the abandoned attempt and the read
    // set it waits on, over a span that strictly contains the scheduler's own
    // `retryParked` entry.)
    const waiting = new Map<number, DebugWaiting>();
    for (const p of this.parked) {
      waiting.set(p.thread.id, { kind: "lock", txn: p.txn, addrs: [...p.addrs].sort() });
    }
    // 2PL locks held per thread (from the address→owner map), so a lock txn's
    // held footprint stays visible in the graph for its whole lock lifetime — the
    // acquire→begin window, the running body, and the commit→release tail.
    const held = new Map<number, Address[]>();
    for (const [addr, tid] of this.locks) {
      const list = held.get(tid);
      if (list) list.push(addr);
      else held.set(tid, [addr]);
    }
    const threads: DebugThread[] = [];
    for (const thread of this.liveThreads.values()) {
      // `pendingTxn` names the lock txn across its whole lifetime (whether or not
      // `currentTxn` is set), so held locks are attributed even while the body runs.
      const heldAddrs = held.get(thread.id);
      const holding: DebugHolding | null =
        thread.pendingTxn !== null && heldAddrs
          ? { txn: thread.pendingTxn, addrs: [...heldAddrs].sort() }
          : null;
      threads.push(
        this.captureThread(
          thread,
          this.threadState(thread, actingThread),
          waiting.get(thread.id) ?? null,
          holding,
          thread.prevAttempt
        )
      );
    }
    threads.sort((a, b) => a.id - b.id);
    return { actingThread, committedVersion: this.store.committedVersion, threads };
  }

  /**
   * What the scheduler is doing with `thread` right now — the annotation the
   * debugger's thread list carries (see `DebugThreadState`).
   *
   * Read off the scheduler's own structures, in the order a thread passes through
   * them: blocked on locks, in the ready queue (at a future time = sleeping),
   * blocked in `retry`, and otherwise the thread whose step we are in.
   *
   * The one place the queues alone fall short is the row that *announces* a
   * `retry` park. Like every blocking transition (see docs/trace-snapshots.md) it
   * is emitted after the state change, but a `retry` parks by yielding an effect,
   * so the scheduler doesn't hold the thread yet when its own `txn-retry` row is
   * recorded. `Thread.prevAttempt` covers that gap — with one qualification: it is
   * deliberately kept alive past the park, through the replacement attempt's
   * `txn-begin` row, so the hand-off shows both attempts at once. `currentTxn`
   * separates the two, being null exactly while the abandoned attempt has no
   * successor yet.
   */
  private threadState(thread: Thread, actingThread: number): DebugThreadState {
    if (this.parked.some((p) => p.thread === thread)) return "parked-lock";
    const entry = this.ready.find((e) => e.thread === thread);
    if (entry) return entry.time > this.now ? "sleeping" : "ready";
    if (this.retryParked.some((p) => p.thread === thread)) return "parked-retry";
    if (thread.currentTxn === null && thread.prevAttempt?.ended === "retry") return "parked-retry";
    // Not queued and not blocked: this is the thread whose step is running. It is
    // normally the acting thread; a woken lock waiter is momentarily here too,
    // between its granted `lock-acquire` and its re-enqueue, and it is the acting
    // thread of that very row.
    return thread.id === actingThread ? "running" : "ready";
  }

  private captureThread(
    thread: Thread,
    state: DebugThreadState,
    waiting: DebugWaiting | null,
    holding: DebugHolding | null,
    prevAttempt: DebugPrevAttempt | null
  ): DebugThread {
    const txn = thread.currentTxn;
    return {
      id: thread.id,
      state,
      entryFn: thread.fnName,
      frames: thread.frames.map((f) => this.captureFrame(f)),
      waiting,
      holding,
      prevAttempt,
      // Read/write sets are captured as addresses (+ buffered write objects, which
      // are copy-on-write and so safe to hold by reference); values are resolved
      // lazily against the retained store, overlaid with these writes.
      txn: txn
        ? {
            id: txn.id,
            readVersion: txn.readVersion,
            reads: [...txn.readSet].sort(),
            writes: [...txn.writes.entries()]
              .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
              .map(([addr, obj]) => ({ addr, obj })),
          }
        : null,
    };
  }

  /** Collect a frame's in-scope locals by walking from its innermost active
   *  scope up to (and including) its root, inner bindings shadowing outer ones.
   *  The walk stops at the frame boundary so a closure doesn't spill the whole
   *  captured environment. Values are captured raw (immutable snapshots) and
   *  rendered/expanded lazily by the debugger. Outermost-first (params first). */
  private captureFrame(f: Frame): DebugFrame {
    const seen = new Set<string>();
    const locals: { name: string; value: Value }[] = [];
    let s: Scope | null = f.scope ?? null;
    while (s) {
      for (const [name, val] of s.ownEntries()) {
        if (seen.has(name)) continue;
        seen.add(name);
        locals.push({ name, value: val });
      }
      if (s === f.rootScope) break;
      s = s.enclosing;
    }
    locals.reverse();
    return { fn: f.fn, line: f.line, source: f.source, locals };
  }

  private handleEffect(thread: Thread, effect: Effect): void {
    this.lastTime = Math.max(this.lastTime, thread.time);
    switch (effect.kind) {
      case "tick":
        this.enqueue(thread, thread.time);
        break;
      case "sleep": {
        const until = thread.time + effect.duration;
        this.emit({
          type: "sleep",
          time: thread.time,
          thread: thread.id,
          duration: effect.duration,
          until,
          line: thread.currentLine,
        }, thread);
        this.enqueue(thread, until);
        break;
      }
      case "spawn":
        this.enqueue(thread, thread.time); // parent continues
        this.enqueue(effect.thread, thread.time); // child becomes runnable now
        break;
      case "acquire":
        if (this.canAcquire(effect.addrs, thread.id)) {
          this.grant(effect.addrs, thread.id);
          this.emit({
            type: "lock-acquire",
            time: thread.time,
            thread: thread.id,
            txn: effect.txn,
            addrs: effect.addrs,
            line: effect.line,
          }, thread);
          this.enqueue(thread, thread.time); // holds its footprint; continue
        } else {
          // Park before emitting, so the snapshot for this `lock-wait` already
          // shows the thread blocked on its footprint — mirroring the acquire
          // path, which grants before it emits. Otherwise the waiting txn would
          // be absent from the graph on its own `wait` row.
          this.parked.push({ thread, addrs: effect.addrs, txn: effect.txn, line: effect.line });
          this.emit({
            type: "lock-wait",
            time: thread.time,
            thread: thread.id,
            txn: effect.txn,
            addrs: effect.addrs,
            line: effect.line,
          }, thread);
        }
        break;
      case "retry":
        // Park until a committed write overlaps the read set (see wakeRetryParked).
        this.retryParked.push({
          thread,
          readSet: new Set(effect.readSet),
          txn: effect.txn,
          line: effect.line,
        });
        break;
      case "release":
        // Emit before releasing, so the snapshot for this `lock-release` still
        // shows the thread holding its footprint (its txn hasn't let go yet) —
        // the release drops it a step later, mirroring acquire/wait.
        const released = this.emit({
          type: "lock-release",
          time: thread.time,
          thread: thread.id,
          txn: effect.txn,
          addrs: effect.addrs,
          line: effect.line,
        }, thread);
        this.release(effect.addrs, thread.id);
        this.enqueue(thread, thread.time); // releaser continues
        this.wakeParked(released);
        break;
    }
  }

  // -- 2PL lock table -----------------------------------------------------

  /** True if every address is free or already held by this same thread. */
  private canAcquire(addrs: Address[], tid: number): boolean {
    for (const a of addrs) {
      const owner = this.locks.get(a);
      if (owner !== undefined && owner !== tid) return false;
    }
    return true;
  }

  private grant(addrs: Address[], tid: number): void {
    for (const a of addrs) this.locks.set(a, tid);
  }

  private release(addrs: Address[], tid: number): void {
    for (const a of addrs) if (this.locks.get(a) === tid) this.locks.delete(a);
  }

  /**
   * After a release, wake any parked thread whose full footprint is now free,
   * in FIFO order. Because the footprint is granted atomically (all-or-nothing),
   * a woken thread never holds a partial set, so there is no hold-and-wait and
   * thus no deadlock. Granting to one waiter may keep a later one blocked; that's
   * fine — it stays parked until the next release.
   */
  private wakeParked(afterRelease: number): void {
    if (this.parked.length === 0) return;
    const waiters = this.parked;
    const stillParked: typeof this.parked = [];
    for (let i = 0; i < waiters.length; i++) {
      const p = waiters[i]!;
      if (this.canAcquire(p.addrs, p.thread.id)) {
        this.grant(p.addrs, p.thread.id);
        // Leave the parked list *before* emitting, the mirror of the `lock-wait`
        // path joining it before emitting: this row's snapshot has to show the
        // thread holding its footprint, not still waiting for what it just got.
        // (The waiters after this one haven't been considered yet, so they stay.)
        this.parked = [...stillParked, ...waiters.slice(i + 1)];
        // The handoff edge: this acquire happened because that release did.
        this.emit({
          type: "lock-acquire",
          time: p.thread.time,
          thread: p.thread.id,
          txn: p.txn,
          addrs: p.addrs,
          line: p.line,
        }, p.thread, { extra: [afterRelease] });
        this.enqueue(p.thread, p.thread.time);
      } else {
        stillParked.push(p);
      }
    }
    this.parked = stillParked;
  }

  /**
   * After a commit, wake every `retry`-parked thread whose read set overlaps the
   * committed writes — the dual of OCC conflict detection (the same read/write-set
   * overlap, used to *resume* a blocked txn rather than *abort* a running one). A
   * woken thread re-enters `execOccTxn`'s loop and re-runs from the top. This is the
   * simple *scan* wake-index from the design note; a per-address watch list is a
   * later optimization behind the same semantics.
   */
  private wakeRetryParked(writes: Iterable<Address>, time: number): void {
    if (this.retryParked.length === 0) return;
    const written = writes instanceof Set ? writes : new Set(writes);
    if (written.size === 0) return;
    const stillParked: typeof this.retryParked = [];
    for (const p of this.retryParked) {
      let overlap = false;
      for (const a of p.readSet) {
        if (written.has(a)) { overlap = true; break; }
      }
      if (overlap) {
        // The commit that woke it is emitted immediately after this returns; the
        // tracker holds the wake until then and hangs the edge off it.
        this.causal?.noteRetryWake(p.thread.id);
        this.enqueue(p.thread, time);
      } else stillParked.push(p);
    }
    this.retryParked = stillParked;
  }

  // -- scheduler helpers --------------------------------------------------

  private enqueue(thread: Thread, time: number): void {
    const seq = this.seq++;
    this.ready.push({ thread, time, seq, priority: this.priorityFor(seq), enabledBy: this.stepIndex });
  }

  /** The seeded priority for the `n`th enqueue — a splitmix32 finalizer over
   *  `(seed, n)`. A pure function of the pair, so the same seed always produces
   *  the same schedule and a different seed produces a genuinely different one. */
  private priorityFor(n: number): number {
    let x = (this.schedSeed + Math.imul(n, 0x9e3779b9)) >>> 0;
    x = Math.imul(x ^ (x >>> 16), 0x85ebca6b) >>> 0;
    x = Math.imul(x ^ (x >>> 13), 0xc2b2ae35) >>> 0;
    return (x ^ (x >>> 16)) >>> 0;
  }

  /**
   * Index of the runnable entry to run next: the smallest `(time, priority)`,
   * unless a `RunOptions.schedule` override names a thread for this step.
   *
   * The two orderings are not the same kind of thing. `time` is virtual time —
   * a thread cannot run before another thread's earlier instant, which is the
   * model of time rather than a scheduling choice, so neither the seed nor an
   * override bends it. `priority` breaks ties among threads runnable at the
   * same instant, and that tie is the whole of the scheduler's freedom.
   *
   * It used to be plain FIFO (`seq`, stamped at `enqueue`), which had a
   * pathology worth remembering: every yield sent a thread to the back of the
   * queue, so making preemption *finer* made execution *more* serial — a thread
   * that yielded often simply fell behind, and `counter.stm` went from six
   * aborts to zero when every statement became an interleaving point. A seeded
   * priority has no such bias, and it is what makes `--seed` mean what everyone
   * assumes it means: before this, the seed fed only the `random()` builtin and
   * every seed produced a byte-identical trace.
   */
  private pickNext(): number {
    let best = 0;
    for (let i = 1; i < this.ready.length; i++) {
      const a = this.ready[i]!;
      const b = this.ready[best]!;
      if (a.time < b.time || (a.time === b.time && a.priority < b.priority)) best = i;
    }
    const forced = this.forcedThread();
    if (forced === null) return best;
    // Honored only among entries at the minimum ready time, so an override can
    // never manufacture an execution the interpreter couldn't have produced.
    const minTime = this.ready[best]!.time;
    for (let i = 0; i < this.ready.length; i++) {
      const e = this.ready[i]!;
      if (e.thread.id === forced && e.time === minTime) return i;
    }
    this.scheduleAbandoned = true; // the thread isn't available; stop forcing
    return best;
  }

  /**
   * The threads that could run now: everything ready at the minimum ready time,
   * best tie-break first. Only computed when causality is on.
   *
   * Virtual time is not a choice — a thread at a later instant cannot run before
   * an earlier one — so the candidates are exactly the entries sharing the
   * minimum time, which is also the set a `schedule` override may pick from. A
   * thread appears once: it is either runnable or it isn't.
   */
  private readyChoices(): number[] {
    let min = Infinity;
    for (const e of this.ready) if (e.time < min) min = e.time;
    return this.ready
      .filter((e) => e.time === min)
      .sort((a, b) => a.priority - b.priority)
      .map((e) => e.thread.id);
  }

  /** The thread the override names for the step about to run, or null when no
   *  override is in force (none given, window passed, or already abandoned). */
  private forcedThread(): number | null {
    if (!this.schedule || this.scheduleAbandoned) return null;
    const i = this.stepIndex - this.schedule.from;
    if (i < 0 || i >= this.schedule.threads.length) return null;
    return this.schedule.threads[i]!;
  }

  // -- thread body --------------------------------------------------------

  private *runThread(fn: ast.FnDecl, args: Value[], thread: Thread): Generator<Effect, void, void> {
    thread.currentLine = fn.span.start.line;
    const scope = new Scope(null);
    fn.params.forEach((p, i) => scope.declare(p.name, args[i] ?? VOID));
    // The thread joins the live set and gets its base frame *before* the
    // `thread-start` event, and leaves only after `thread-end`, so both bracket
    // events (and every event in between) see it in a debugger snapshot.
    this.liveThreads.set(thread.id, thread);
    this.pushFrame(thread, fn.name, scope, this.frameLoc(fn));
    try {
      this.emit({
        type: "thread-start",
        time: thread.time,
        thread: thread.id,
        fn: fn.name,
        line: fn.span.start.line,
      }, thread);
      // A spawned thread is fire-and-forget: its return value is discarded (to hand a
      // result back to the spawner, the stdlib `spawn` has the body write it into a
      // shared `Future` cell). We still drain the body for its side effects.
      yield* this.execBlock(fn.body, scope, thread);
      this.emit({
        type: "thread-end",
        time: thread.time,
        thread: thread.id,
        fn: fn.name,
        line: fn.span.start.line,
      }, thread);
    } finally {
      this.popFrame(thread);
      this.liveThreads.delete(thread.id);
    }
  }

  /** Where a frame for `fn` should point: its declaration span when that is
   *  located (user code), else its name span. With stdlib spans now retained both
   *  are real; the fallback only guards a rare synthetic offset-0 span. */
  private frameLoc(fn: ast.FnDecl): ast.Span {
    return this.isLocated(fn.span) ? fn.span : fn.nameSpan;
  }

  // -- statements ---------------------------------------------------------

  private *execBlock(block: ast.Block, scope: Scope, thread: Thread): StmtGen {
    const inner = new Scope(scope);
    for (const stmt of block.stmts) {
      const c = yield* this.execStmt(stmt, inner, thread);
      if (c.type !== "normal") return c;
    }
    return NORMAL;
  }

  private *execStmt(stmt: ast.Stmt, scope: Scope, thread: Thread): StmtGen {
    // Track the line we're on so every event this statement produces, and the
    // innermost stack frame, report where execution is (blocks span many lines,
    // so let their contents win). A statement with a real position — user code
    // and, now that stdlib spans are retained, prelude code alike — moves the
    // frame's line/source; this is what lets a `raise` deeper down report each
    // caller's line (see `captureStack`) and the debugger highlight the exact line.
    const top = thread.frames[thread.frames.length - 1];
    if (stmt.kind !== "block") {
      thread.currentLine = stmt.span.start.line;
      if (top && this.isLocated(stmt.span)) {
        top.line = stmt.span.start.line;
        top.source = stmt.span.source;
      }
    }
    // The debugger also reads each frame's innermost scope to list its locals.
    if (this.debugEnabled && top) top.scope = scope;

    // Interleaving point: yield before every statement so another thread can
    // run. Unconditional — a thread outside a transaction is no less
    // preemptible than one inside it, and pretending otherwise made unguarded
    // code look atomic.
    //
    // Statement boundaries are not the finest granularity: the store accesses
    // *within* one are interleaving points too (see the `tick`s in `execAssign`
    // and the field-read case of `evalExpr`), because a real OS thread is
    // preempted by a timer interrupt at essentially any instruction rather than
    // on reaching a statement boundary. That is what makes an unguarded
    // `c.n += 1` genuinely racy here and not merely racy in principle.
    //
    // What this does *not* model is weak memory: an interleaving, however fine,
    // still puts each thread's accesses in program order in one global order.
    // Real hardware reorders (x86-64 is TSO, ARM weaker) and so do compilers, so
    // there are outcomes no interleaving explains. Sequential consistency is the
    // deliberate idealization — it is what an STM runtime's fences buy you
    // inside a `txn`, and the gap is confined to unguarded code.
    yield { kind: "tick" };

    switch (stmt.kind) {
      case "block":
        return yield* this.execBlock(stmt, scope, thread);
      case "let": {
        const v = yield* this.evalExpr(stmt.value, scope, thread);
        scope.declare(stmt.name, v);
        return NORMAL;
      }
      case "assign":
        return yield* this.execAssign(stmt, scope, thread);
      case "exprStmt":
        yield* this.evalExpr(stmt.expr, scope, thread);
        return NORMAL;
      case "return": {
        const value = stmt.value ? yield* this.evalExpr(stmt.value, scope, thread) : VOID;
        return { type: "return", value };
      }
      case "if":
        return yield* this.execIf(stmt, scope, thread);
      case "while":
        return yield* this.execWhile(stmt, scope, thread);
      case "for":
        return yield* this.execFor(stmt, scope, thread);
      case "match":
        return yield* this.execMatch(stmt, scope, thread);
      case "go":
        return yield* this.execGo(stmt, scope, thread);
      case "txn":
        return yield* this.execTxn(stmt, scope, thread);
      case "retry":
        // Unwind (via a thrown sentinel) to the enclosing `execOccTxn`, which
        // parks this thread on its read set. `retry` yields no value.
        throw new RetrySignal(stmt.span.start.line);
      case "raise":
        return yield* this.execRaise(stmt, scope, thread);
      case "try":
        return yield* this.execTry(stmt, scope, thread);
      case "assert":
        return yield* this.execAssert(stmt, scope, thread);
    }
  }

  /** `assert cond` / `assert cond, "message"`: evaluate the condition, then record
   *  an `assert` trace event with its verdict and label. This is the *soft* form —
   *  a failed assertion does not unwind the thread; it is a recorded observation
   *  the outcome layer reads, which is what makes an assertion a comparable end
   *  state for the race detector's timeline-tree search. */
  private *execAssert(stmt: ast.AssertStmt, scope: Scope, thread: Thread): StmtGen {
    const cond = yield* this.evalExpr(stmt.cond, scope, thread);
    const passed = cond.kind === "bool" ? cond.value : false;
    let message = "";
    if (stmt.message) {
      const m = yield* this.evalExpr(stmt.message, scope, thread);
      message = m.kind === "string" ? m.value : yield* this.showValue(m, thread, stmt.message.span);
    }
    this.emit(
      { type: "assert", time: thread.time, thread: thread.id, passed, message, line: thread.currentLine },
      thread
    );
    if (this.echo) {
      const verdict = passed ? "PASS" : "FAIL";
      console.log(
        `[T${thread.id} t=${thread.time.toFixed(2)}] assert ${verdict}${message ? `: ${message}` : ""}`
      );
    }
    return NORMAL;
  }

  /** `raise e` / `raise e from cause`: build the in-flight error — capturing the
   *  stack *here*, at the raise site — and unwind to the nearest matching `catch`.
   *  The message is rendered now, while a generator context is available to call the
   *  value's `Error.message()`; by the time the signal reaches the top of a thread
   *  there is nowhere left to run STM code. */
  private *execRaise(stmt: ast.RaiseStmt, scope: Scope, thread: Thread): StmtGen {
    const value = yield* this.evalExpr(stmt.value, scope, thread);
    const causeValue = stmt.cause ? yield* this.evalExpr(stmt.cause, scope, thread) : null;
    let cause: RaisedError | null = null;
    if (causeValue) {
      // `raise x from e` inside a `catch e { … }` chains onto the *caught* error, so
      // the cause keeps the trace of where it was originally raised. A cause that
      // isn't a currently-handled error (a freshly built value) simply has none.
      cause =
        this.caughtErrorFor(causeValue, thread) ??
        (yield* this.raisedError(causeValue, null, thread, stmt.cause!.span, []));
    }
    // `raise e` on the error a `catch` is currently handling is a **re-raise** — the
    // explicit spelling of "let this one through" — and it keeps the stack captured
    // where the error was first raised. Re-stamping the trace here would delete every
    // frame between the original raise and this handler, which is the whole reason the
    // trace exists. (`throw e` in Java and `raise e` in Python preserve it likewise.)
    const original = this.caughtErrorFor(value, thread);
    if (original) throw new RaiseSignal(cause ? { ...original, cause } : original);
    throw new RaiseSignal(
      yield* this.raisedError(value, cause, thread, stmt.value.span, this.captureStack(thread))
    );
  }

  /** The in-flight error a value denotes, if it is one a `catch` up the stack is
   *  currently handling — how a re-raise and a `from` cause recover the trace taken at
   *  the original raise site. Innermost handler wins.
   *
   *  Matching is by value: a heap error compares by identity, while a *value type*
   *  (the usual shape — `IOError`, `JSONError`, every all-`key` error struct) compares
   *  by its fields, so a rebuilt-identical error counts as the same one. That is what
   *  value semantics mean in this language, not an approximation. */
  private caughtErrorFor(value: Value, thread: Thread): RaisedError | null {
    for (let i = thread.caught.length - 1; i >= 0; i--) {
      const c = thread.caught[i]!;
      if (valuesEqual(c.value, value)) return c;
    }
    return null;
  }

  /** Package a raised value: its catchable type name, its `Error.message()`, the
   *  cause it chains onto, and the stack captured at the raise site. */
  private *raisedError(
    value: Value,
    cause: RaisedError | null,
    thread: Thread,
    span: ast.Span,
    stack: Frame[]
  ): Generator<Effect, RaisedError, void> {
    const typeName = this.showableTypeName(value);
    let message: string;
    if (typeName && this.methods.get(typeName)?.has("message")) {
      const m = yield* this.invokeUserMethod(typeName, value, "message", [], thread, span);
      message = m.kind === "string" ? m.value : yield* this.showValue(m, thread, span);
    } else {
      // No `Error` impl (the checker rejects that, but the interpreter also runs
      // unchecked in tests): fall back to the value's own rendering.
      message = yield* this.showValue(value, thread, span);
    }
    return { value, typeName, message, cause, stack };
  }

  /** The current STM call stack, innermost frame first — a snapshot, since the live
   *  frames keep mutating as the stack unwinds. Only the trace-relevant fields are
   *  kept; the debugger's scope refs are dropped so an error value holds no live
   *  interpreter state. */
  private captureStack(thread: Thread): Frame[] {
    return thread.frames.map((f) => ({ fn: f.fn, line: f.line, source: f.source })).reverse();
  }

  /** `try { … } catch e { … }`: run the body; on a raise, bind the error and run the
   *  handler. The handler takes every error — selecting on its type is the handler's
   *  own business, and `raise e` sends back what it doesn't want.
   *  A `retry` is *not* an exception: it passes straight through to the enclosing
   *  transaction, so a `try` inside a `txn` never swallows the STM blocking
   *  primitive. */
  private *execTry(stmt: ast.TryStmt, scope: Scope, thread: Thread): StmtGen {
    const depth = thread.frames.length;
    try {
      return yield* this.execBlock(stmt.body, scope, thread);
    } catch (e) {
      if (!(e instanceof RaiseSignal)) throw e;
      // The frames of everything the raise unwound past are gone; the handler runs in
      // this frame. (Each generator's `finally` normally pops its own frame, but a
      // native builtin that raises may unwind without one, so re-level here.)
      thread.frames.length = depth;
      const inner = new Scope(scope);
      inner.declare(stmt.handler.name, e.error.value);
      thread.caught.push(e.error);
      try {
        return yield* this.execBlock(stmt.handler.body, inner, thread);
      } finally {
        thread.caught.pop();
      }
    }
  }

  private *execAssign(stmt: ast.AssignStmt, scope: Scope, thread: Thread): StmtGen {
    const rhs = yield* this.evalExpr(stmt.value, scope, thread);

    if (stmt.target.kind === "ident") {
      const name = stmt.target.name;
      if (stmt.op === "=") {
        if (!scope.assign(name, rhs)) scope.declare(name, rhs);
        return NORMAL;
      }
      const current = scope.get(name);
      if (current === undefined) throw new RuntimeError(`undefined variable \`${name}\``, stmt.span);
      // A compound assign (`x += e`) has no `BinaryExpr` node, so no elaboration
      // entry: the legacy path re-derives the overload (and agrees with the Go
      // backend, which does the same for compound assignments).
      const next = yield* this.evalBinaryOp(stmt.op[0]!, current, rhs, undefined, thread, stmt.span);
      scope.assign(name, next);
      return NORMAL;
    }

    // field assignment: obj.field (op)= rhs
    const target = stmt.target;
    if (target.kind !== "field") {
      throw new RuntimeError("invalid assignment target", target.span);
    }
    const objVal = yield* this.evalExpr(target.object, scope, thread);
    if (objVal.kind === "record") {
      throw new RuntimeError(
        `cannot assign to field \`${target.field}\` of \`${objVal.typeName}\`: value types are immutable`,
        target.span
      );
    }
    if (objVal.kind !== "ptr") {
      throw new RuntimeError(`cannot assign to field of non-object`, target.span);
    }
    // A plain `a.f = v` reads the object only to rebuild its record — an
    // interpreter artifact, not a load the program asked for, so it is neither an
    // interleaving point nor a `read` row (`silent`). A compound `a.f += v` is a
    // genuine load/modify/store: it ticks, and its load is a real read.
    const plain = stmt.op === "=";
    if (!plain) yield { kind: "tick" };
    const obj = this.readStruct(thread, objVal.addr, target.span, plain);
    let newVal = rhs;
    if (stmt.op !== "=") {
      const current = obj.fields[target.field];
      if (current === undefined) {
        throw new RuntimeError(`no field \`${target.field}\``, target.span);
      }
      // The load has landed and the store hasn't: this is the window a real
      // `+=` has between its load and its store, and the one an unguarded
      // counter loses updates in.
      yield { kind: "tick" };
      newVal = yield* this.evalBinaryOp(stmt.op[0]!, current, rhs, undefined, thread, target.span);
    }
    if (!(target.field in obj.fields)) {
      throw new RuntimeError(
        `\`${obj.typeName}\` has no field \`${target.field}\``,
        target.span
      );
    }
    const nextFields = { ...obj.fields, [target.field]: newVal };
    this.writeObject(thread, objVal.addr, {
      kind: "struct",
      typeName: obj.typeName,
      fields: nextFields,
    });
    return NORMAL;
  }

  private *execIf(stmt: ast.IfStmt, scope: Scope, thread: Thread): StmtGen {
    const cond = yield* this.evalExpr(stmt.cond, scope, thread);
    if (truthy(cond)) {
      return yield* this.execBlock(stmt.then, scope, thread);
    }
    if (stmt.else) {
      if (stmt.else.kind === "if") return yield* this.execIf(stmt.else, scope, thread);
      return yield* this.execBlock(stmt.else, scope, thread);
    }
    return NORMAL;
  }

  private *execWhile(stmt: ast.WhileStmt, scope: Scope, thread: Thread): StmtGen {
    for (;;) {
      yield { kind: "tick" }; // fairness: never monopolize the scheduler
      const cond = yield* this.evalExpr(stmt.cond, scope, thread);
      if (!truthy(cond)) return NORMAL;
      const c = yield* this.execBlock(stmt.body, scope, thread);
      if (c.type !== "normal") return c;
    }
  }

  private *execFor(stmt: ast.ForStmt, scope: Scope, thread: Thread): StmtGen {
    const source = yield* this.evalExpr(stmt.iterable, scope, thread);
    // Peephole: the stdlib `Range` yields `start, start+1, …, end-1`. Recognizing it
    // lets the loop run as a native counted loop, skipping the `RangeIter` cursor —
    // and, crucially inside a `txn`, the per-step versioned read/write on that cursor
    // that would otherwise join the transaction's footprint. Compiled backends get the
    // same rewrite at the Core level (`monomorphize.lowerRangeFor`); this interpreter
    // walks the AST, so it carries its own copy. An optimization only: `Range` stays an
    // ordinary stdlib `Iterable` (see `asStdlibRange`).
    const rng = this.asStdlibRange(source, thread);
    if (rng) {
      for (let i = rng.start; i < rng.end; i++) {
        const iterScope = new Scope(scope);
        iterScope.declare(stmt.varName, int(i));
        const c = yield* this.execBlock(stmt.body, iterScope, thread);
        if (c.type !== "normal") return c;
      }
      return NORMAL;
    }
    // Otherwise obtain one iterator for whatever the source is, then run a single loop
    // over it — no per-source branch. Each call to next() returns Option<T>:
    // Some(value) continues the loop, None ends it.
    const it = yield* this.getIterator(source, this.elab.loops.get(stmt), thread, stmt.iterable.span);
    for (;;) {
      const opt = yield* it.next();
      if (opt.kind !== "enum" || opt.variant !== "Some") return NORMAL;
      const iterScope = new Scope(scope);
      iterScope.declare(stmt.varName, opt.fields["value"]!);
      const c = yield* this.execBlock(stmt.body, iterScope, thread);
      if (c.type !== "normal") return c;
    }
  }

  /** The bounds of a stdlib `Range` value, or null if `v` isn't one. Reads `start`
   *  and `end` once (a normal versioned read of the range object) so the caller can
   *  drive a native counted loop instead of the `RangeIter` cursor. Fires only when
   *  the stdlib `Range` is in effect — a program that shadows `Range`/`RangeIter`
   *  keeps whatever semantics it defined, driven through the ordinary protocol. */
  private asStdlibRange(v: Value, thread: Thread): { start: number; end: number } | null {
    if (!this.stdlibRangeActive || v.kind !== "ptr") return null;
    const entry = this.readEntry(thread, v.addr);
    if (entry?.value?.kind !== "struct" || entry.value.typeName !== "Range") return null;
    const start = entry.value.fields["start"];
    const end = entry.value.fields["end"];
    if (!start || !isNumeric(start) || !end || !isNumeric(end)) return null;
    return { start: Math.trunc(start.value), end: Math.trunc(end.value) };
  }

  /**
   * The iterator for a value, following the loop's plan from the Elaboration (the
   * same classification the checker made and the Go backend lowers): a built-in
   * collection's *native* snapshot iterator; an `Iterable` whose `iter()` yields a
   * fresh iterator to drive; or a value that is itself an `Iterator`. The plan
   * decides *which protocol* (by trait, not by a method's name); the receiver's
   * runtime type still selects the concrete `iter`/`next` impl to invoke.
   */
  private *getIterator(
    v: Value,
    plan: IterResolution | undefined,
    thread: Thread,
    span: ast.Span
  ): Generator<Effect, RuntimeIterator<Effect>, void> {
    let typeName: string | null = null;
    // A container iterator cursor is already a driver — `for x in set.iter()`.
    if (v.kind === "collIter") return v.iter;
    if (v.kind === "ptr") {
      const marker = this.readEntry(thread, v.addr);
      if (marker?.value?.kind === "collection") {
        return this.collectionIterator(v.addr, marker.value.container, thread);
      }
      if (marker?.value?.kind === "struct") typeName = marker.value.typeName;
    } else if (v.kind === "record") typeName = v.typeName;
    else if (v.kind === "enum") typeName = v.enumName;

    // Classify from the elaboration when available (trait-based); the transitional
    // fallback (method presence) only runs when no plan was supplied — the `run`
    // path always supplies one.
    const kind: "iterable" | "iterator" | null =
      plan?.kind === "iterable" || plan?.kind === "iterator"
        ? plan.kind
        : typeName && this.methods.get(typeName)?.has("iter")
          ? "iterable"
          : typeName && this.methods.get(typeName)?.has("next")
            ? "iterator"
            : null;

    if (typeName && kind === "iterable") {
      // `iter()` returns a fresh `Iterator`; drive that value directly.
      const iter = yield* this.invokeUserMethod(typeName, v, "iter", [], thread, span);
      const innerType = this.receiverTypeName(iter, thread);
      if (innerType) return this.methodIterator(innerType, iter, thread, span);
    } else if (typeName && kind === "iterator") {
      return this.methodIterator(typeName, v, thread, span);
    }
    throw new RuntimeError("value is not iterable", span);
  }

  /** The user-type name a value dispatches on: a heap struct's stored type, a value
   *  type's `typeName`, or an enum's `enumName`. Null for anything else. */
  private receiverTypeName(v: Value, thread: Thread): string | null {
    if (v.kind === "ptr") {
      const marker = this.readEntry(thread, v.addr);
      return marker?.value?.kind === "struct" ? marker.value.typeName : null;
    }
    if (v.kind === "record") return v.typeName;
    if (v.kind === "enum") return v.enumName;
    if (v.kind === "collIter") return v.typeName;
    return null;
  }

  /** An iterator backed by a user type's `next` method (returns `Option<T>`). */
  private methodIterator(typeName: string, iter: Value, thread: Thread, span: ast.Span): RuntimeIterator<Effect> {
    const self = this;
    return {
      *next() {
        return yield* self.invokeUserMethod(typeName, iter, "next", [], thread, span);
      },
    };
  }

  private collectionIterator(collAddr: Address, container: string, thread: Thread): CollectionIterator {
    return new CollectionIterator(
      collAddr,
      container,
      (addr) => this.readEntry(thread, addr),
      (addr) => this.liveChildren(thread, addr),
    );
  }

  private *execMatch(stmt: ast.MatchStmt, scope: Scope, thread: Thread): StmtGen {
    const subject = yield* this.evalExpr(stmt.subject, scope, thread);
    if (subject.kind !== "enum") {
      throw new RuntimeError("match subject is not an enum value", stmt.subject.span);
    }
    for (const arm of stmt.arms) {
      // A `_` arm matches any variant; a named arm only its own.
      if (!arm.wildcard && arm.variant !== subject.variant) continue;
      const armScope = new Scope(scope);
      for (const b of arm.bindings) {
        if (!(b.field in subject.fields)) {
          throw new RuntimeError(
            `variant \`${subject.variant}\` has no field \`${b.field}\``,
            b.fieldSpan
          );
        }
        armScope.declare(b.name, subject.fields[b.field]!);
      }
      return yield* this.execBlock(arm.body, armScope, thread);
    }
    throw new RuntimeError(`no match arm for variant \`${subject.variant}\``, stmt.span);
  }

  private *execGo(stmt: ast.GoStmt, scope: Scope, thread: Thread): StmtGen {
    // Fire-and-forget: spawn the thread; its return value is discarded.
    yield* this.spawnThread(stmt.call, stmt.span, scope, thread);
    return NORMAL;
  }

  /** The `go` statement's spawn: resolve the callee, evaluate its arguments in the
   *  *current* thread, create the child thread (runnable now), record the trace
   *  event, and yield the `spawn` effect. */
  private *spawnThread(call: ast.CallExpr, span: ast.Span, scope: Scope, thread: Thread): Generator<Effect, Thread, void> {
    if (thread.currentTxn) {
      // A backstop: the checker's `spawn` effect rejects this statically (a `go`
      // reached from a `txn` is a diagnostic, not a runtime error), so reaching here
      // means the program was run without a check pass.
      throw new RuntimeError("cannot spawn a thread (`go`) inside a transaction", span);
    }
    if (call.callee.kind !== "ident") {
      throw new RuntimeError("`go` requires a direct function call", span);
    }
    const fn = this.fns.get(call.callee.name);
    if (!fn) throw new RuntimeError(`unknown function \`${call.callee.name}\``, span);
    const args = yield* this.evalArgs(call, scope, thread);

    const child = new Thread(this.nextThreadId++, fn.name);
    child.gen = this.runThread(fn, args, child);
    this.emit({
      type: "spawn",
      time: thread.time,
      parent: thread.id,
      child: child.id,
      fn: fn.name,
      line: span.start.line,
    }, thread);
    yield { kind: "spawn", thread: child };
    return child;
  }

  private *execTxn(stmt: ast.TxnStmt, scope: Scope, thread: Thread): StmtGen {
    if (thread.currentTxn) {
      // Flatten nested transactions into the enclosing one.
      return yield* this.execBlock(stmt.body, scope, thread);
    }
    const strat = this.strategies?.get(stmt);
    if (strat?.kind === "lock") {
      return yield* this.execLockTxn(stmt, scope, thread, strat.footprint);
    }
    return yield* this.execOccTxn(stmt, scope, thread);
  }

  /**
   * Optimistic (OCC) transaction: snapshot, buffer, validate, retry on conflict.
   * When 2PL locks are in play (hybrid), a read/write of an address another
   * thread currently holds locked is also a conflict — a lock txn is mid-flight
   * over that object, so committing over it would race (mirrors the Go commit,
   * where "a set lock means another committer owns it").
   */
  private *execOccTxn(stmt: ast.TxnStmt, scope: Scope, thread: Thread): StmtGen {
    let attempt = 0;
    // The previous attempt of this same `txn` statement, if any. Each attempt is a
    // fresh transaction with its own id, so this is what links the chain together:
    // a re-run after a `retry` park (the parked txn "reincarnated") or after an
    // abort. Reported on `txn-begin` as `prevTxn`.
    let prevTxn: number | null = null;
    for (;;) {
      attempt++;
      const txn = new Transaction(this.nextTxnId++, this.store);
      thread.currentTxn = txn;
      this.emit({
        type: "txn-begin",
        time: thread.time,
        thread: thread.id,
        txn: txn.id,
        readVersion: txn.readVersion,
        ...(prevTxn !== null ? { prevTxn } : {}),
        line: stmt.span.start.line,
      }, thread);
      // The attempt this one replaces has now handed off, so stop carrying it. It
      // stayed set through the emit above so that row's snapshot shows the pair:
      // the abandoned attempt — parked (with the read set it waited on) or aborted
      // (with the read that went stale) — beside the successor replacing it.
      thread.prevAttempt = null;

      let completion: Completion;
      try {
        completion = yield* this.execBlock(stmt.body, new Scope(scope), thread);
      } catch (e) {
        if (e instanceof RaiseSignal) {
          // An exception unwinding out of a transaction abandons it: the attempt's
          // buffered writes are dropped with the `Transaction` and nothing commits,
          // so the raise cannot publish a half-finished state. The checker forbids
          // this statically (catch it *inside* the `txn`); this is the runtime's
          // answer for an unchecked program, and it is the conservative one.
          this.abandonTxn(txn.id, attempt, e, thread);
          throw e;
        }
        if (!(e instanceof RetrySignal)) throw e;
        // `retry`: abandon this attempt (its buffered writes are simply dropped
        // with the Transaction) and park on the read set. The scheduler re-enqueues
        // us when a committed write overlaps it; we then loop and re-run the body.
        const readSet = [...txn.readSet];
        // Park *before* emitting, so this row's snapshot already shows the txn
        // waiting on its read set rather than still running (mirroring the
        // lock-wait path). `prevAttempt` keeps the abandoned attempt — and what
        // it waits for — visible for the whole parked span.
        thread.currentTxn = null;
        thread.prevAttempt = {
          txn: txn.id,
          reads: [...readSet].sort(),
          writes: [...txn.writes.keys()].sort(),
          readVersion: txn.readVersion,
          ended: "retry",
        };
        prevTxn = txn.id;
        this.emit({
          type: "txn-retry",
          time: thread.time,
          thread: thread.id,
          txn: txn.id,
          reads: txn.readSet.size,
          line: e.line,
        }, thread);
        // Guard against a lost wakeup: another thread may have committed a write to
        // our read set *between* our snapshot and now (the write that would have
        // woken us can land while this attempt is still mid-flight). If our read set
        // is already stale, re-run immediately instead of parking on it — otherwise
        // no future write would ever arrive and we'd deadlock. This is the standard
        // STM validate-before-block step (the dual of commit-time validation).
        if (txn.validate() !== null) continue;
        yield { kind: "retry", readSet, txn: txn.id, line: e.line };
        // Woken by a committed write overlapping the read set: re-run from the top.
        // `prevAttempt` deliberately stays set until the next `txn-begin` is
        // emitted — the thread is woken but hasn't re-run yet, and the hand-off row
        // needs the read set it was waiting on.
        continue;
      }

      const stale = txn.validate();
      const locked = stale === null ? this.lockedByOther(txn, thread.id) : null;
      if (stale === null && locked === null) {
        const commitVersion = txn.commit();
        this.wakeRetryParked(txn.writes.keys(), thread.time);
        this.commits++;
        const at = this.emit({
          type: "txn-commit",
          time: thread.time,
          thread: thread.id,
          txn: txn.id,
          commitVersion,
          reads: txn.readSet.size,
          writes: txn.writes.size,
          line: stmt.span.start.line,
        }, thread);
        // This commit published every address it wrote: later reads of those
        // versions hang their reads-from edge off it, and a transaction it
        // invalidates hangs its abort off it.
        this.causal?.noteCommit(txn.writes.keys(), commitVersion, at);
        thread.currentTxn = null;
        return completion; // propagate `return` out of the txn if any
      }

      this.aborts++;
      // A validation abort is caused by whichever commit superseded the stale
      // read. (A lock conflict has no such event — the holder hasn't committed.)
      const doomedBy =
        stale !== null ? this.causal?.publisher(stale, this.store.latestBegin(stale)) : undefined;
      this.emit({
        type: "txn-abort",
        time: thread.time,
        thread: thread.id,
        txn: txn.id,
        reason: stale !== null ? `read ${stale} changed` : `${locked} locked`,
        attempt,
        line: stmt.span.start.line,
      }, thread, doomedBy !== undefined ? { extra: [doomedBy] } : undefined);
      thread.currentTxn = null;
      prevTxn = txn.id;
      // Retain the dead attempt for the next `begin`'s row: its read set (with the
      // stale read that doomed it) and the writes it never got to publish.
      thread.prevAttempt = {
        txn: txn.id,
        reads: [...txn.readSet].sort(),
        writes: [...txn.writes.keys()].sort(),
        readVersion: txn.readVersion,
        ended: "abort",
      };
      // retry
    }
  }

  /**
   * Pessimistic (2PL) transaction: lock the whole statically-known footprint,
   * run the body exactly once (no retry, so side effects are safe), publish, and
   * unlock. Because it holds every object it touches for the whole body, no
   * validation is needed and it can never abort. This is the interpreter's
   * emulation of the Go backend's `RunLocked{1,2,N}`.
   */
  private *execLockTxn(
    stmt: ast.TxnStmt,
    scope: Scope,
    thread: Thread,
    footprint: string[]
  ): StmtGen {
    const txnId = this.nextTxnId++;
    const addrs = this.resolveFootprint(footprint, scope, stmt.span);
    // `pendingTxn` attributes the held footprint to this txn in snapshots where
    // `currentTxn` isn't the source of truth — the whole lock lifetime outside the
    // begun body: the acquire→begin window and the commit→release tail. Cleared in
    // `finally`, once the lock is handed back.
    thread.pendingTxn = txnId;
    try {
      // Parks until the whole footprint is held (the scheduler emits the
      // lock-wait / lock-acquire trace events around this yield).
      yield { kind: "acquire", addrs, txn: txnId, line: stmt.span.start.line };

      const txn = new Transaction(txnId, this.store);
      thread.currentTxn = txn;
      this.emit({
        type: "txn-begin",
        time: thread.time,
        thread: thread.id,
        txn: txnId,
        readVersion: txn.readVersion,
        line: stmt.span.start.line,
      }, thread);

      let completion: Completion;
      try {
        completion = yield* this.execBlock(stmt.body, new Scope(scope), thread);
      } catch (e) {
        // A raise unwinding out of a *lock* txn must still hand the footprint back, or
        // every other thread waiting on it deadlocks. Nothing is published (the
        // buffered writes die with the `Transaction`), as in the OCC path above.
        if (e instanceof RaiseSignal) {
          this.abandonTxn(txnId, 1, e, thread);
          yield { kind: "release", addrs, txn: txnId, line: stmt.span.start.line };
        }
        throw e;
      }

      // No validate / retry: the locks guarantee the footprint didn't move.
      const commitVersion = txn.writes.size > 0 ? txn.commit() : this.store.committedVersion;
      this.wakeRetryParked(txn.writes.keys(), thread.time);
      this.commits++;
      const at = this.emit({
        type: "txn-commit",
        time: thread.time,
        thread: thread.id,
        txn: txnId,
        commitVersion,
        reads: txn.readSet.size,
        writes: txn.writes.size,
        line: stmt.span.start.line,
      }, thread);
      this.causal?.noteCommit(txn.writes.keys(), commitVersion, at);
      thread.currentTxn = null;
      yield { kind: "release", addrs, txn: txnId, line: stmt.span.start.line };
      return completion;
    } finally {
      thread.pendingTxn = null;
    }
  }

  /** Drop a transaction an exception is unwinding out of: count it as an abort, note
   *  it in the trace, and detach it from the thread. The attempt's buffered writes go
   *  with the `Transaction` object, so nothing it wrote is ever published. */
  private abandonTxn(txnId: number, attempt: number, raised: RaiseSignal, thread: Thread): void {
    this.aborts++;
    this.trace.record({
      type: "txn-abort",
      time: thread.time,
      thread: thread.id,
      txn: txnId,
      reason: `raised ${raised.error.typeName ?? "error"}`,
      attempt,
      line: thread.currentLine,
    });
    thread.currentTxn = null;
  }

  /** Resolve footprint root names to a sorted, deduped list of held addresses. */
  private resolveFootprint(names: string[], scope: Scope, span: ast.Span): Address[] {
    const addrs = new Set<Address>();
    for (const name of names) {
      const v = scope.get(name);
      if (v === undefined) throw new RuntimeError(`undefined variable \`${name}\``, span);
      if (v.kind !== "ptr") throw new RuntimeError(`cannot lock non-object \`${name}\``, span);
      addrs.add(v.addr);
    }
    return [...addrs].sort();
  }

  /**
   * The first address in `txn`'s read/write set that another thread holds a 2PL
   * lock on, or null. Always null under "occ" (the lock table stays empty).
   */
  private lockedByOther(txn: Transaction, tid: number): Address | null {
    if (this.locks.size === 0) return null;
    for (const addr of txn.readSet) {
      const owner = this.locks.get(addr);
      if (owner !== undefined && owner !== tid) return addr;
    }
    for (const addr of txn.writes.keys()) {
      const owner = this.locks.get(addr);
      if (owner !== undefined && owner !== tid) return addr;
    }
    return null;
  }

  // -- expressions --------------------------------------------------------

  private *evalExpr(expr: ast.Expr, scope: Scope, thread: Thread): ExprGen {
    switch (expr.kind) {
      case "int":
        return int(expr.value);
      case "float":
        return float(expr.value);
      case "string":
        return str(expr.value);
      case "char":
        return char(expr.value);
      case "bool":
        return bool(expr.value);
      case "ident": {
        const v = scope.get(expr.name);
        if (v !== undefined) return v;
        // A bare top-level function name used as a value is a first-class
        // function reference (functions capture nothing, so the name suffices).
        if (this.fns.has(expr.name)) return { kind: "fn", name: expr.name };
        throw new RuntimeError(`undefined variable \`${expr.name}\``, expr.span);
      }
      case "unary": {
        const operand = yield* this.evalExpr(expr.operand, scope, thread);
        if (expr.op === "-") {
          if (!isNumeric(operand)) throw new RuntimeError("cannot negate non-number", expr.span);
          return operand.kind === "int" ? int(-operand.value) : float(-operand.value);
        }
        return bool(!truthy(operand));
      }
      case "binary":
        return yield* this.evalBinary(expr, scope, thread);
      case "field": {
        const objVal = yield* this.evalExpr(expr.object, scope, thread);
        // A value type is inline: read its field directly, no store access.
        if (objVal.kind === "record") {
          const fv = objVal.fields[expr.field];
          if (fv === undefined) {
            throw new RuntimeError(`\`${objVal.typeName}\` has no field \`${expr.field}\``, expr.span);
          }
          return fv;
        }
        if (objVal.kind !== "ptr") {
          throw new RuntimeError(`cannot read field \`${expr.field}\` of non-object`, expr.span);
        }
        // A real load, and its own interleaving point: the operands of
        // `a.x + b.y` can be read either side of another thread's write, which
        // is what the machine does and a statement-granular model hides.
        yield { kind: "tick" };
        const entry = this.readEntry(thread, objVal.addr);
        if (entry?.value?.kind === "collection") {
          throw new RuntimeError(
            `cannot read field \`${expr.field}\` of ${entry.value.container}`,
            expr.span
          );
        }
        if (!entry || entry.value?.kind !== "struct") {
          throw new RuntimeError(`expected an object at ${objVal.addr}`, expr.span);
        }
        const fv = entry.value.fields[expr.field];
        if (fv === undefined) {
          throw new RuntimeError(`\`${entry.value.typeName}\` has no field \`${expr.field}\``, expr.span);
        }
        return fv;
      }
      case "list":
        return yield* this.evalListLit(expr, scope, thread);
      case "brace":
        return yield* this.evalBraceLit(expr, scope, thread);
      case "call":
        return yield* this.evalCall(expr, scope, thread);
      case "lambda":
        // A closure captures the scope it is created in (by reference), so its
        // body sees the enclosing function's variables when later called.
        return { kind: "closure", fn: expr, env: scope };
      case "ifExpr": {
        // An if-expression: evaluate the condition, then the taken branch — whose
        // value is the whole expression's. The untaken branch is never evaluated.
        const cond = yield* this.evalExpr(expr.cond, scope, thread);
        return yield* this.evalExpr(truthy(cond) ? expr.then : expr.else, scope, thread);
      }
    }
  }

  private *evalBinary(expr: ast.BinaryExpr, scope: Scope, thread: Thread): ExprGen {
    // Short-circuit boolean operators.
    if (expr.op === "&&") {
      const l = yield* this.evalExpr(expr.left, scope, thread);
      if (!truthy(l)) return bool(false);
      return bool(truthy(yield* this.evalExpr(expr.right, scope, thread)));
    }
    if (expr.op === "||") {
      const l = yield* this.evalExpr(expr.left, scope, thread);
      if (truthy(l)) return bool(true);
      return bool(truthy(yield* this.evalExpr(expr.right, scope, thread)));
    }
    const l = yield* this.evalExpr(expr.left, scope, thread);
    const r = yield* this.evalExpr(expr.right, scope, thread);
    return yield* this.evalBinaryOp(expr.op, l, r, this.elab.binaries.get(expr), thread, expr.span);
  }

  /** Apply a binary operator. Whether it is an operator-overload dispatch (and to
   *  which trait method) comes from the Elaboration — the same decision the checker
   *  made and the Go backend lowers — not re-derived here: arithmetic to
   *  `Add`/`Sub`/… (`a.add(b)`), `==`/`!=` to `Eq.eq`, `<`/`>`/`<=`/`>=` to
   *  `Ord.cmp` (comparing against 0). The method name is from the plan; the left
   *  operand's *runtime* type selects the impl. Absent a plan (no elaboration, or a
   *  deferred query operand), fall back to the built-in `binaryOp`. */
  private *evalBinaryOp(
    op: string,
    l: Value,
    r: Value,
    res: BinaryResolution | undefined,
    thread: Thread,
    span: ast.Span
  ): ExprGen {
    if (res?.kind === "overload") {
      const typeName = this.operandTypeName(l, thread);
      if (typeName) {
        if (op === "==" || op === "!=") {
          const e = yield* this.invokeUserMethod(typeName, l, res.method, [r], thread, span);
          return bool(op === "==" ? truthy(e) : !truthy(e));
        }
        if (op === "<" || op === ">" || op === "<=" || op === ">=") {
          const c = yield* this.invokeUserMethod(typeName, l, res.method, [r], thread, span);
          if (!isNumeric(c)) throw new RuntimeError("`cmp` must return a number", span);
          return bool(compareToZero(op, c.value));
        }
        return yield* this.invokeUserMethod(typeName, l, res.method, [r], thread, span);
      }
    }
    // Native (or unelaborated): a deferred-query operand defers here too.
    if (res === undefined) return yield* this.evalBinaryOpLegacy(op, l, r, thread, span);
    return this.binaryOp(op, l, r, thread, span);
  }

  /** Transitional operator dispatch for when no Elaboration is supplied (the `run`
   *  path always supplies one). Re-derives the overload from `OPERATOR_DISPATCH`. */
  private *evalBinaryOpLegacy(op: string, l: Value, r: Value, thread: Thread, span: ast.Span): ExprGen {
    const d = OPERATOR_DISPATCH[op];
    if (d && this.definedMethodNames.has(d.method)) {
      const typeName = this.operandTypeName(l, thread);
      if (typeName && this.methods.get(typeName)?.has(d.method)) {
        if (d.kind === "arith") return yield* this.invokeUserMethod(typeName, l, d.method, [r], thread, span);
        if (d.kind === "eq") {
          const e = yield* this.invokeUserMethod(typeName, l, "eq", [r], thread, span);
          return bool(op === "==" ? truthy(e) : !truthy(e));
        }
        const c = yield* this.invokeUserMethod(typeName, l, "cmp", [r], thread, span);
        if (!isNumeric(c)) throw new RuntimeError("`cmp` must return a number", span);
        return bool(compareToZero(op, c.value));
      }
    }
    return this.binaryOp(op, l, r, thread, span);
  }

  /** The runtime type name of a binary operator's left operand, for overload
   *  dispatch, or null (scalars never overload). A heap struct's type is read
   *  from the store (a genuine read of the operand). */
  private operandTypeName(v: Value, thread: Thread): string | null {
    if (v.kind === "record") return v.typeName;
    if (v.kind === "enum") return v.enumName;
    if (v.kind === "ptr") {
      const entry = this.readEntry(thread, v.addr);
      if (entry?.value?.kind === "struct") return entry.value.typeName;
    }
    return null;
  }

  private binaryOp(
    op: string,
    a: Value,
    b: Value,
    thread: Thread,
    span: ast.Span
  ): Value {
    switch (op) {
      case "==":
        return bool(valuesEqual(a, b));
      case "!=":
        return bool(!valuesEqual(a, b));
    }

    // String concatenation.
    if (op === "+" && a.kind === "string" && b.kind === "string") {
      return str(a.value + b.value);
    }

    // Char ordering — compare by code point (`==`/`!=` already handled above).
    if (a.kind === "char" && b.kind === "char") {
      switch (op) {
        case "<": return bool(a.value < b.value);
        case ">": return bool(a.value > b.value);
        case "<=": return bool(a.value <= b.value);
        case ">=": return bool(a.value >= b.value);
        default: throw new RuntimeError(`operator \`${op}\` is not defined on \`char\``, span);
      }
    }

    // String ordering — lexicographic. JS compares by UTF-16 code unit, which
    // agrees with Go's byte-wise order for all BMP text. (`==`/`!=`, and `+`
    // concatenation, are handled above; arithmetic falls through to an error.)
    if (a.kind === "string" && b.kind === "string") {
      switch (op) {
        case "<": return bool(a.value < b.value);
        case ">": return bool(a.value > b.value);
        case "<=": return bool(a.value <= b.value);
        case ">=": return bool(a.value >= b.value);
      }
    }

    // Bool ordering — `false < true`.
    if (a.kind === "bool" && b.kind === "bool") {
      const x = a.value ? 1 : 0;
      const y = b.value ? 1 : 0;
      switch (op) {
        case "<": return bool(x < y);
        case ">": return bool(x > y);
        case "<=": return bool(x <= y);
        case ">=": return bool(x >= y);
      }
    }

    if (!isNumeric(a) || !isNumeric(b)) {
      throw new RuntimeError(`operator \`${op}\` needs numbers`, span);
    }
    const x = a.value;
    const y = b.value;
    const resultFloat = a.kind === "float" || b.kind === "float";
    const num = (n: number): Value => (resultFloat ? float(n) : int(n));
    switch (op) {
      case "+":
        return num(x + y);
      case "-":
        return num(x - y);
      case "*":
        return num(x * y);
      case "/":
        if (y === 0) throw new RuntimeError("division by zero", span);
        return resultFloat ? float(x / y) : int(Math.trunc(x / y));
      case "%":
        if (y === 0) throw new RuntimeError("division by zero", span);
        return num(x % y);
      case "<":
        return bool(x < y);
      case ">":
        return bool(x > y);
      case "<=":
        return bool(x <= y);
      case ">=":
        return bool(x >= y);
      default:
        throw new RuntimeError(`unknown operator \`${op}\``, span);
    }
  }

  // -- calls --------------------------------------------------------------

  private *evalArgs(call: ast.CallExpr, scope: Scope, thread: Thread): Generator<Effect, Value[], void> {
    const out: Value[] = [];
    for (const arg of call.args) out.push(yield* this.evalExpr(arg.value, scope, thread));
    return out;
  }

  private *evalCall(expr: ast.CallExpr, scope: Scope, thread: Thread): ExprGen {
    if (expr.callee.kind === "ident") {
      const name = expr.callee.name;

      // Calling a first-class function value held in a variable / parameter,
      // e.g. a param `f` invoked as `f(x)`. A fn-valued binding shadows any
      // same-named global, so it's checked before the builtins/constructors.
      const bound = scope.get(name);
      if (bound?.kind === "fn" || bound?.kind === "closure") {
        const args = yield* this.evalArgs(expr, scope, thread);
        return yield* this.callValue(bound, args, thread, expr.span);
      }

      // sleep is the one call that yields a scheduler effect.
      if (name === "sleep") {
        if (thread.currentTxn) {
          throw new RuntimeError("cannot `sleep` inside a transaction", expr.span);
        }
        const arg = expr.args[0]
          ? yield* this.evalExpr(expr.args[0].value, scope, thread)
          : float(0);
        if (!isNumeric(arg)) throw new RuntimeError("sleep expects a number", expr.span);
        yield { kind: "sleep", duration: arg.value };
        return VOID;
      }

      if (this.structs.has(name)) {
        return yield* this.constructStruct(name, expr, scope, thread);
      }
      if (this.variantIndex.has(name)) {
        return yield* this.constructEnum(name, expr, scope, thread);
      }
      if (ast.isGenericContainer(name)) {
        return this.newCollection(thread, name, expr.typeArgs.map(typeArgName));
      }
      const fn = this.fns.get(name);
      if (fn) {
        const args = yield* this.evalArgs(expr, scope, thread);
        const completion = yield* this.callUserFn(fn, args, thread, expr.span);
        return completion.type === "return" ? completion.value : VOID;
      }
      // `print` / `str` render their arguments via the `Show` trait when a value's
      // type implements it (else the default rendering). They're generators (a
      // `show` method can `yield`), so they live here rather than in `builtins`.
      if (name === "str") {
        const arg = expr.args[0] ? yield* this.evalExpr(expr.args[0].value, scope, thread) : VOID;
        return str(yield* this.showValue(arg, thread, expr.span));
      }
      if (name === "print") {
        const args = yield* this.evalArgs(expr, scope, thread);
        const parts: string[] = [];
        for (const a of args) parts.push(yield* this.showValue(a, thread, expr.span));
        const message = parts.join(" ");
        this.emit({
          type: "print",
          time: thread.time,
          thread: thread.id,
          message,
          line: thread.currentLine,
        }, thread);
        if (this.echo) console.log(`[T${thread.id} t=${thread.time.toFixed(2)}] ${message}`);
        return VOID;
      }
      // `orElse` composes two blocking actions with partial rollback. It runs the
      // branch thunks (which may `yield` and `retry`), so it lives here with `print`/
      // `str` rather than in the plain `builtins` table.
      if (name === "orElse") {
        return yield* this.evalOrElse(expr, scope, thread);
      }
      const builtin = this.builtins[name];
      if (builtin) {
        const args = yield* this.evalArgs(expr, scope, thread);
        return builtin(args, thread, expr.span);
      }
      throw new RuntimeError(`unknown function or type \`${name}\``, expr.span);
    }

    // method call: obj.method(args) — dispatchMethod also handles a struct field
    // holding a function value (when there's no method of that name).
    if (expr.callee.kind === "field") {
      const recv = yield* this.evalExpr(expr.callee.object, scope, thread);
      const method = expr.callee.field;
      const args = yield* this.evalArgs(expr, scope, thread);
      return yield* this.dispatchMethod(recv, method, args, thread, expr.span);
    }

    // Any other callee expression that evaluates to a callable value, e.g. an
    // immediately-invoked lambda `((x) -> x)(5)`.
    const callee = yield* this.evalExpr(expr.callee, scope, thread);
    if (callee.kind === "fn" || callee.kind === "closure") {
      const args = yield* this.evalArgs(expr, scope, thread);
      return yield* this.callValue(callee, args, thread, expr.span);
    }
    throw new RuntimeError("invalid call target", expr.span);
  }

  /** Call a function value — a top-level function reference or a closure. */
  private *callValue(callee: Value, args: Value[], thread: Thread, span: ast.Span): ExprGen {
    if (callee.kind === "fn") {
      const fn = this.fns.get(callee.name);
      if (!fn) throw new RuntimeError(`unknown function \`${callee.name}\``, span);
      const completion = yield* this.callUserFn(fn, args, thread, span);
      return completion.type === "return" ? completion.value : VOID;
    }
    if (callee.kind === "closure") {
      const { fn, env } = callee;
      if (args.length !== fn.params.length) {
        throw new RuntimeError(`closure expects ${fn.params.length} argument(s), got ${args.length}`, span);
      }
      // Nest a fresh scope in the captured environment and bind the parameters.
      // A closure is deliberately *not* given its own stack frame: it reads as a
      // transparent call site in a `raise` trace (see exceptions.test.ts), so the
      // enclosing function's frame is the one that shows.
      const scope = new Scope(env as Scope);
      fn.params.forEach((p, i) => scope.declare(p.name, args[i]!));
      // A block body runs statements and returns via `return` (void if it never
      // does); an expression body's value is the closure's result.
      if (fn.body.kind === "block") {
        const completion = yield* this.execBlock(fn.body, scope, thread);
        return completion.type === "return" ? completion.value : VOID;
      }
      return yield* this.evalExpr(fn.body, scope, thread);
    }
    throw new RuntimeError("value is not callable", span);
  }

  /**
   * `orElse(a, b)` — the `select`/composable-blocking primitive (see
   * docs/design/concurrency-via-retry.md). Run `a`; if it `retry`s, undo **only**
   * `a`'s tentative writes — its reads stay in the transaction's read set, so a
   * later change to what `a` observed still re-runs the txn — then run `b`. If `b`
   * also `retry`s, the signal propagates to the enclosing `execOccTxn`, which parks
   * on the union of both branches' reads (the read set was never rolled back). If
   * `a` succeeds, `b` never runs. The one asymmetry that makes it work: **reads
   * accumulate, writes roll back.**
   */
  private *evalOrElse(expr: ast.CallExpr, scope: Scope, thread: Thread): ExprGen {
    const txn = thread.currentTxn;
    if (!txn) throw new RuntimeError("`orElse` may only be used inside a `txn`", expr.span);
    if (expr.args.length !== 2) {
      throw new RuntimeError(`\`orElse\` expects 2 branches, got ${expr.args.length}`, expr.span);
    }
    const a = yield* this.evalExpr(expr.args[0]!.value, scope, thread);
    const b = yield* this.evalExpr(expr.args[1]!.value, scope, thread);
    // Savepoint the tentative write set (values included: a branch may overwrite an
    // entry buffered before the `orElse`, which a length-only checkpoint couldn't undo).
    const savepoint = new Map(txn.writes);
    try {
      return yield* this.callValue(a, [], thread, expr.span);
    } catch (e) {
      if (!(e instanceof RetrySignal)) throw e; // an exception aborts the whole txn
      // `a` blocked: roll back its writes, keep its reads, try `b`. A `retry` from
      // `b` unwinds past here to `execOccTxn`, which parks on the union of the reads.
      // Roll back *before* emitting, so this row's snapshot shows the state the
      // event describes — the losing branch's tentative writes already discarded,
      // its reads still in the set ("reads accumulate, writes roll back").
      txn.writes.clear();
      for (const [addr, value] of savepoint) txn.writes.set(addr, value);
      this.emit({
        type: "orelse-retry",
        time: thread.time,
        thread: thread.id,
        txn: txn.id,
        line: e.line,
      }, thread);
      return yield* this.callValue(b, [], thread, expr.span);
    }
  }

  private *callUserFn(
    fn: ast.FnDecl,
    args: Value[],
    thread: Thread,
    span: ast.Span
  ): Generator<Effect, Completion, void> {
    if (args.length !== fn.params.length) {
      throw new RuntimeError(
        `\`${fn.name}\` expects ${fn.params.length} argument(s), got ${args.length}`,
        span
      );
    }
    const scope = new Scope(null);
    fn.params.forEach((p, i) => scope.declare(p.name, args[i]!));
    return yield* this.withFrame(fn.name, this.frameLoc(fn), scope, thread, () =>
      this.execBlock(fn.body, scope, thread)
    );
  }

  /** Run a call's body with a stack frame pushed for it, popping on the way out —
   *  including when a `raise` (or any other signal) unwinds through it, which is what
   *  keeps `thread.frames` an accurate picture of the live stack at a raise site.
   *  `name` is how the frame reads in a trace (`Type.method` for a method) and the
   *  debugger's call stack; `loc` is the declaration span (real for user and — now
   *  that stdlib spans are retained — prelude code alike); `rootScope` is the call's
   *  base scope, the boundary the debugger walks locals up to. */
  private *withFrame<T>(
    name: string,
    loc: ast.Span,
    rootScope: Scope,
    thread: Thread,
    body: () => Generator<Effect, T, void>
  ): Generator<Effect, T, void> {
    this.pushFrame(thread, name, rootScope, loc);
    try {
      return yield* body();
    } finally {
      this.popFrame(thread);
    }
  }

  // -- constructors -------------------------------------------------------

  private *constructStruct(name: string, call: ast.CallExpr, scope: Scope, thread: Thread): ExprGen {
    const decl = this.structs.get(name)!;
    // Evaluate args in order (generators), then map each to its field.
    const values: Value[] = [];
    for (const arg of call.args) values.push(yield* this.evalExpr(arg.value, scope, thread));
    const byName = new Map<string, Value>();
    call.args.forEach((arg, i) => {
      const fieldName = arg.name ?? decl.fields[i]?.name;
      if (!fieldName) throw new RuntimeError(`too many arguments to \`${name}\``, call.span);
      byName.set(fieldName, values[i]!);
    });

    const fields: Record<string, Value> = {};
    for (const f of decl.fields) {
      // Every field is provided or default-filled by the checker (which rejects an
      // omitted field that has no default), so a missing one means the program was
      // run without a check pass — there is no zero-value fallback.
      if (!byName.has(f.name)) throw new RuntimeError(`\`${name}\` is missing field \`${f.name}\``, call.span);
      fields[f.name] = byName.get(f.name)!;
    }
    // A value type lives inline: return it by value, with no store allocation.
    if (this.valueTypes.has(name)) return { kind: "record", typeName: name, fields };
    const addr = this.store.allocAddress();
    this.writeObject(thread, addr, { kind: "struct", typeName: name, fields });
    return ptr(addr);
  }

  private *constructEnum(name: string, call: ast.CallExpr, scope: Scope, thread: Thread): ExprGen {
    const { enumName, variant } = this.variantIndex.get(name)!;
    const values: Value[] = [];
    for (const arg of call.args) values.push(yield* this.evalExpr(arg.value, scope, thread));
    const byName = new Map<string, Value>();
    call.args.forEach((arg, i) => {
      const fieldName = arg.name ?? variant.fields[i]?.name;
      if (fieldName) byName.set(fieldName, values[i]!);
    });
    const fields: Record<string, Value> = {};
    for (const f of variant.fields) {
      // As with structs: an omitted field with no default is a checker error, so
      // reaching here with one missing means no check pass ran.
      if (!byName.has(f.name)) throw new RuntimeError(`\`${name}\` is missing field \`${f.name}\``, call.span);
      fields[f.name] = byName.get(f.name)!;
    }
    return { kind: "enum", enumName, variant: variant.name, fields };
  }


  /**
   * A list literal `[a, b, c]`: a fresh `List<T>` holding the elements in order.
   *
   * Deliberately not the `List<T>()`-then-`.append` it desugars to on paper. The
   * collection is unreachable by any other thread while it is being built — it was
   * allocated inside this expression and nothing else holds a reference — so the
   * elements go straight into their index cells and the key-set version is bumped
   * ONCE, rather than `append`'s read-modify-write of `#keys` per element. Same
   * contents and the same conflict behaviour (a fresh cell can't conflict), with one
   * structural write instead of N.
   */
  private *evalListLit(expr: ast.ListLit, scope: Scope, thread: Thread): ExprGen {
    const values: Value[] = [];
    for (const e of expr.elements) values.push(yield* this.evalExpr(e, scope, thread));
    // The element type is inferred, so its display name (for traces) comes from the
    // checker's recorded type rather than a written-out type argument.
    const t = this.elab.types.get(expr);
    const elem = t?.k === "generic" ? t.args[0] : undefined;
    const addr = this.newCollectionAddr(thread, "List", elem ? [typeArgDisplay(elem)] : []);
    values.forEach((v, i) => this.putEntry(thread, addr, int(i), v));
    if (values.length > 0) this.bumpKeys(thread, addr);
    return ptr(addr);
  }

  /** A brace literal — `{k: v, …}` (a `Dict<K, V>`) or `{a, b, …}` (a `Set<T>`).
   *
   *  Both are the same shape in the store: one entry cell per key, where a dict's key
   *  is written and a set's element *is* the key (its cell holds `void`, exactly as
   *  `.add` stores it). Entries are evaluated in source order — for a dict, each
   *  entry's key then its value — and written in that order, so a repeated dict key
   *  overwrites and a repeated set element collapses, just as the `.set`/`.add`
   *  sequence this replaces would. Built in bulk for the same reason as a list
   *  literal (see `evalListLit`): one key-set bump, not one per entry. */
  private *evalBraceLit(expr: ast.BraceLit, scope: Scope, thread: Thread): ExprGen {
    const t = this.elab.types.get(expr);
    // The parser leaves an empty `{}` undecided; the checker resolved it from context.
    const container = expr.container ?? (t?.k === "generic" ? t.name : "Dict");
    const isDict = container === "Dict";
    const what = isDict ? "dict literal" : "set literal";
    const entries: { key: Value; value: Value }[] = [];
    for (const en of expr.entries) {
      // A set's element is its key, so it is the one that must be encodable.
      const keyExpr = en.key ?? en.value;
      const key = this.checkKey(yield* this.evalExpr(keyExpr, scope, thread), what, keyExpr.span);
      entries.push({ key, value: en.key ? yield* this.evalExpr(en.value, scope, thread) : VOID });
    }
    const args = t?.k === "generic" ? t.args : [];
    const arity = isDict ? 2 : 1;
    const addr = this.newCollectionAddr(
      thread,
      container,
      args.length === arity ? args.map(typeArgDisplay) : []
    );
    for (const { key, value } of entries) this.putEntry(thread, addr, key, value);
    if (entries.length > 0) this.bumpKeys(thread, addr);
    return ptr(addr);
  }

  private newCollection(thread: Thread, container: string, typeArgs: string[]): Value {
    return ptr(this.newCollectionAddr(thread, container, typeArgs));
  }

  private newCollectionAddr(thread: Thread, container: string, typeArgs: string[]): Address {
    const addr = this.store.allocAddress();
    this.writeObject(thread, addr, {
      kind: "collection",
      container,
      typeArgs: typeArgs.length ? typeArgs : ["any"],
    });
    return addr;
  }

  // ---- shared collection mechanics (used by Set, Dict, and List) ------------
  //
  // Every collection is a marker plus one child cell per entry, keyed under the
  // marker's address. These helpers are the whole flat-keyspace / per-entry MVCC
  // machinery; the per-container methods below are thin wrappers that only decide
  // how a key/value maps onto an entry. A read or write here touches exactly ONE
  // key, so the store validates per entry — two transactions on different keys
  // never conflict.

  /**
   * The address of `key`'s entry in the collection at `collAddr`: the marker's
   * namespace plus one path segment derived from the KEY value (see
   * `keySegment`) — e.g. coll /4, object element /1 -> /4/o1. Because the segment
   * comes from the value, the key may be an object OR a primitive.
   */
  private entryAddr(collAddr: Address, key: Value): Address {
    return `${collAddr}/${keySegment(key)}`;
  }

  /** The collection's key-set version address — a sibling of the marker, NOT a
   *  child (no `/`), so it stays out of `liveChildren`. Iteration/size read it;
   *  inserts and removes bump it. */
  private keysAddr(collAddr: Address): Address {
    return `${collAddr}#keys`;
  }

  /**
   * Advance the key-set version so a concurrent iteration/size (which reads it)
   * is conflict-detected against this structural change. A BLIND write (never
   * read here), so inserts/removes of DIFFERENT keys don't conflict with each
   * other — only a range read is invalidated.
   */
  private bumpKeys(thread: Thread, collAddr: Address): void {
    this.writeObject(thread, this.keysAddr(collAddr), { kind: "keyset" });
  }

  /** Read `key`'s entry cell, or undefined if absent/tombstoned. */
  private getEntry(
    thread: Thread,
    collAddr: Address,
    key: Value
  ): { key: Value; value: Value } | undefined {
    const existing = this.readEntry(thread, this.entryAddr(collAddr, key));
    return existing && existing.value?.kind === "entry" ? existing.value : undefined;
  }

  /** Write (create or overwrite) `key`'s entry cell. */
  private putEntry(thread: Thread, collAddr: Address, key: Value, value: Value): void {
    this.writeObject(thread, this.entryAddr(collAddr, key), { kind: "entry", key, value });
  }

  /** Tombstone `key`'s entry; returns whether it was present. */
  private deleteEntry(thread: Thread, collAddr: Address, key: Value): boolean {
    if (!this.getEntry(thread, collAddr, key)) return false;
    this.writeBatch(thread, [[this.entryAddr(collAddr, key), null]]);
    this.bumpKeys(thread, collAddr); // a removed key changes the key-set
    return true;
  }

  /** A key must be an object, a primitive, a value type, or an enum — the
   *  encodable kinds (`keySegment`). An object keys by identity; a primitive,
   *  value type, or enum by value (hashing its fields). */
  private checkKey(key: Value | undefined, method: string, span: ast.Span): Value {
    if (
      !key ||
      (key.kind !== "ptr" &&
        key.kind !== "int" &&
        key.kind !== "float" &&
        key.kind !== "bool" &&
        key.kind !== "string" &&
        key.kind !== "char" &&
        key.kind !== "record" &&
        key.kind !== "enum")
    ) {
      throw new RuntimeError(`\`${method}\` expects an object, primitive, value-type, or enum key`, span);
    }
    return key;
  }

  // -- method dispatch ----------------------------------------------------

  /**
   * Dispatch `recv.method(args)`. A value type (`record`) or `enum` receiver only
   * has user-defined `impl` methods. A `ptr` receiver is either a built-in
   * collection (Set/Dict/List methods) or a heap struct, which — like value
   * types and enums — dispatches to its `impl` methods by runtime type.
   */
  private *dispatchMethod(
    recv: Value,
    method: string,
    args: Value[],
    thread: Thread,
    span: ast.Span
  ): ExprGen {
    if (recv.kind === "record") return yield* this.invokeUserMethod(recv.typeName, recv, method, args, thread, span);
    if (recv.kind === "enum") return yield* this.invokeUserMethod(recv.enumName, recv, method, args, thread, span);
    // Primitive receivers carry the prelude's impl methods, keyed under the type's
    // name: `str`/`char` (inherent impls in `strings.stm`), and `int`/`float`/
    // `char`/`str`/`bool` (the `impl Ord for …` blocks in `core.stm`, so `x.cmp(y)`
    // works on a scalar). `string` maps to the `str` alias its impls are written on.
    if (recv.kind === "string") return yield* this.invokeUserMethod("str", recv, method, args, thread, span);
    if (recv.kind === "char") return yield* this.invokeUserMethod("char", recv, method, args, thread, span);
    if (recv.kind === "int") return yield* this.invokeUserMethod("int", recv, method, args, thread, span);
    if (recv.kind === "float") return yield* this.invokeUserMethod("float", recv, method, args, thread, span);
    if (recv.kind === "bool") return yield* this.invokeUserMethod("bool", recv, method, args, thread, span);
    // A container iterator cursor (`SetIter`/`DictIter`, from `Set`/`Dict.iter()`):
    // `next()` drives the native snapshot. This is what backs a set/dict flowing
    // through the `Iterable` `map`/`filter` adapters, which pull `source.next()`.
    if (recv.kind === "collIter") {
      if (method === "next") return yield* recv.iter.next();
      throw new RuntimeError(`\`${method}\` is not a method on \`${recv.typeName}\``, span);
    }
    if (recv.kind === "ptr") {
      // Read the marker (written once at creation, never rewritten — no conflict)
      // to tell a built-in collection from a heap struct.
      const marker = this.readEntry(thread, recv.addr);
      if (marker?.value?.kind === "collection") {
        const container = marker.value.container;
        // `iter()` hands back a native cursor value (`SetIter`/`DictIter`/`ListIter`)
        // — the `Iterable` impl's `builtin fn iter`. Intercept it before the method
        // lookup below (its declared body is empty), so a container is a real
        // `Iterable` and the trait's `map`/`filter`/`reduce` defaults compose over it.
        if (method === "iter" && container in CONTAINER_ITER_CURSORS) {
          return {
            kind: "collIter",
            typeName: CONTAINER_ITER_CURSORS[container]!,
            iter: this.collectionIterator(recv.addr, container, thread),
          };
        }
        // Built-in container methods take precedence; a user `impl` (e.g.
        // `impl<T> Trait for Set<T>`) — and the inherited `Iterable` defaults
        // (`map`/`filter`/`reduce`) — fill in any method the container lacks.
        if (!isBuiltinContainerMethod(container, method) && this.methods.get(container)?.has(method)) {
          return yield* this.invokeUserMethod(container, recv, method, args, thread, span);
        }
        switch (container) {
          case "Set":
            return this.setMethod(recv.addr, method, args, thread, span);
          case "Dict":
            return this.dictMethod(recv.addr, method, args, thread, span);
          case "List":
            return this.listMethod(recv.addr, method, args, thread, span);
          default:
            throw new RuntimeError(`\`${method}\` is not a method on this value`, span);
        }
      }
      if (marker?.value?.kind === "struct") {
        const typeName = marker.value.typeName;
        // A real method wins; otherwise a field holding a function value is
        // callable — `h.run(x)` where `run` is a `(int) -> int` field. Reuses the
        // marker already read, so no extra store access.
        if (!this.methods.get(typeName)?.has(method)) {
          const fv = marker.value.fields[method];
          if (fv && (fv.kind === "fn" || fv.kind === "closure")) {
            return yield* this.callValue(fv, args, thread, span);
          }
        }
        return yield* this.invokeUserMethod(typeName, recv, method, args, thread, span);
      }
    }
    throw new RuntimeError(`\`${method}\` is not a method on this value`, span);
  }

  /** Invoke a user-defined `impl` method: bind `self` to the receiver and the
   *  remaining parameters to the arguments, then run the body. */
  private *invokeUserMethod(
    typeName: string,
    recv: Value,
    method: string,
    args: Value[],
    thread: Thread,
    span: ast.Span
  ): ExprGen {
    const fn = this.methods.get(typeName)?.get(method);
    if (!fn) throw new RuntimeError(`\`${typeName}\` has no method \`${method}\``, span);
    // A `builtin` method is implemented natively, like a builtin free function.
    // It's plugged in by the key `Type_method` (matching how it's lowered for the
    // Go backend), receiving the receiver as its first argument — so `conn.send(x)`
    // reaches the same native code a free `send(conn, x)` would.
    if (fn.builtin) {
      const key = `${typeName}_${method}`;
      const native = this.builtins[key];
      if (!native) throw new RuntimeError(`no native implementation for builtin method \`${typeName}.${method}\``, span);
      return native([recv, ...args], thread, span);
    }
    if (args.length !== fn.params.length) {
      throw new RuntimeError(
        `\`${method}\` expects ${fn.params.length} argument(s), got ${args.length}`,
        span
      );
    }
    const scope = new Scope(null);
    scope.declare("self", recv);
    fn.params.forEach((p, i) => scope.declare(p.name, args[i]!));
    const completion = yield* this.withFrame(`${typeName}.${method}`, this.frameLoc(fn), scope, thread, () =>
      this.execBlock(fn.body, scope, thread)
    );
    return completion.type === "return" ? completion.value : VOID;
  }

  /** Render a value as a string for `print`/`str`: its `Show` impl's `show(self)`
   *  when its runtime type implements `Show`, otherwise the default rendering.
   *  When no `Show` impls exist at all, this is exactly the old print path. */
  private *showValue(v: Value, thread: Thread, span: ast.Span): Generator<Effect, string, void> {
    if (this.hasShow) {
      const typeName = this.showableTypeName(v);
      if (typeName && this.methods.get(typeName)?.has("show")) {
        const r = yield* this.invokeUserMethod(typeName, v, "show", [], thread, span);
        if (r.kind === "string") return r.value;
      }
    }
    if (v.kind === "string") return v.value;
    // A char renders as its glyph (so `str('x')` is `"x"`, `print('x')` shows x),
    // not the quoted `'x'` that `displayValue` uses for traces.
    if (v.kind === "char") return charString(v.value);
    return displayValue(v, (addr) => this.store.read(addr, this.store.committedVersion)?.value ?? undefined);
  }

  /** The runtime type name to dispatch `show` on, or null (primitives, and
   *  transient query values). A pointer's type is peeked from the committed store
   *  — an untraced read, so `print` never perturbs a transaction's read set. */
  private showableTypeName(v: Value): string | null {
    if (v.kind === "record") return v.typeName;
    if (v.kind === "enum") return v.enumName;
    if (v.kind === "ptr") {
      const obj = this.store.read(v.addr, this.store.committedVersion)?.value;
      if (obj?.kind === "struct") return obj.typeName;
      if (obj?.kind === "collection") return obj.container;
    }
    return null;
  }

  // A Set stores membership only: each entry maps its element key to VOID.
  private setMethod(
    setAddr: Address,
    method: string,
    args: Value[],
    thread: Thread,
    span: ast.Span
  ): Value {
    switch (method) {
      case "add": {
        const elem = this.checkKey(args[0], "add", span);
        if (this.getEntry(thread, setAddr, elem)) return VOID; // already present
        this.putEntry(thread, setAddr, elem, VOID);
        this.bumpKeys(thread, setAddr); // a new member changes the key-set
        return VOID;
      }
      case "remove":
        return bool(this.deleteEntry(thread, setAddr, this.checkKey(args[0], "remove", span)));
      case "contains":
        return bool(!!this.getEntry(thread, setAddr, this.checkKey(args[0], "contains", span)));
      case "size":
        // Depend on the key-set version so a concurrent insert/remove conflicts.
        this.readEntry(thread, this.keysAddr(setAddr));
        return int(this.liveChildren(thread, setAddr).length);
      default:
        throw new RuntimeError(`unknown method \`${method}\` on Set`, span);
    }
  }

  // A Dict maps each key entry to its value.
  private dictMethod(
    dictAddr: Address,
    method: string,
    args: Value[],
    thread: Thread,
    span: ast.Span
  ): Value {
    switch (method) {
      case "set": {
        const key = this.checkKey(args[0], "set", span);
        this.putEntry(thread, dictAddr, key, args[1] ?? VOID);
        // Conservative: a blind set can't tell a new key from an overwrite, so
        // bump on every set (an overwrite may thus conflict with a concurrent
        // key iteration — safe). Refine by reading the entry first if needed.
        this.bumpKeys(thread, dictAddr);
        return VOID;
      }
      case "get": {
        // A miss is a value, not an error: `Some(value)` when present, `None` when
        // absent — the caller matches on it (see `Dict.get` in collections.stm).
        const key = this.checkKey(args[0], "get", span);
        const entry = this.getEntry(thread, dictAddr, key);
        return entry ? optionSome(entry.value) : optionNone();
      }
      case "has":
      case "contains":
        return bool(!!this.getEntry(thread, dictAddr, this.checkKey(args[0], method, span)));
      case "remove":
        return bool(this.deleteEntry(thread, dictAddr, this.checkKey(args[0], "remove", span)));
      case "size":
        // Depend on the key-set version so a concurrent insert/remove conflicts.
        this.readEntry(thread, this.keysAddr(dictAddr));
        return int(this.liveChildren(thread, dictAddr).length);
      default:
        throw new RuntimeError(`unknown method \`${method}\` on Dict`, span);
    }
  }

  // A List keys each element by its integer index (0-based). It reuses the shared
  // per-entry machinery: element `i` lives at `/list/n<i>` (`keySegment(int(i))`),
  // so `get`/`set` of one index touch exactly that cell — different indices never
  // conflict. `append` READS the key-set version (its length is the live-child
  // count) and then bumps it, making it a read-modify-write on `#keys`: two
  // concurrent appends both read the same length, target the same index, and so
  // conflict on `#keys` (one aborts and retries at the next index) — the order of a
  // list is therefore always well-defined. There is no `remove`, so the indices
  // stay a contiguous `0..size-1` and the child count is exactly the next index.
  private listMethod(
    listAddr: Address,
    method: string,
    args: Value[],
    thread: Thread,
    span: ast.Span
  ): Value {
    switch (method) {
      case "append": {
        // Read the key-set version so this append conflicts with a concurrent one
        // (both would target the same index); the next index is the live count.
        this.readEntry(thread, this.keysAddr(listAddr));
        const index = this.liveChildren(thread, listAddr).length;
        this.putEntry(thread, listAddr, int(index), args[0] ?? VOID);
        this.bumpKeys(thread, listAddr);
        return VOID;
      }
      case "get": {
        const entry = this.getEntry(thread, listAddr, this.listIndex(args[0], "get", span));
        if (!entry) throw new RuntimeError("get: index out of range", span);
        return entry.value;
      }
      case "set": {
        const key = this.listIndex(args[0], "set", span);
        // Replace an existing element only — reads just this index's cell, so a
        // `set` of one index never conflicts with a `set`/`get` of another (nor
        // with `append`, which touches `#keys` and a fresh cell).
        if (!this.getEntry(thread, listAddr, key)) throw new RuntimeError("set: index out of range", span);
        this.putEntry(thread, listAddr, key, args[1] ?? VOID);
        return VOID;
      }
      case "size":
        // Depend on the key-set version so a concurrent append conflicts.
        this.readEntry(thread, this.keysAddr(listAddr));
        return int(this.liveChildren(thread, listAddr).length);
      default:
        throw new RuntimeError(`unknown method \`${method}\` on List`, span);
    }
  }

  /** A List index argument: an `int` (used as the entry key). A non-int index is a
   *  runtime error, mirroring the checker's `int`-typed `index` parameter. */
  private listIndex(index: Value | undefined, method: string, span: ast.Span): Value {
    if (!index || index.kind !== "int") {
      throw new RuntimeError(`\`${method}\` expects an int index`, span);
    }
    return index;
  }

  // -- store access (traced) ----------------------------------------------

  /**
   * Read `addr`, recording the OCC dependency and (unless `silent`) emitting a
   * `read` row.
   *
   * `silent` is for the object load a plain `a.f = v` does only to carry the
   * struct's *other* fields through the write — the value of the field being set
   * is not read, so surfacing a `read` there is an interpreter artifact that
   * reads as a spurious load in the trace and a stray edge in the causal graph.
   * The dependency stays in the read set: at object granularity, writing one
   * field still rests on the object's version (a concurrent change to it must
   * still conflict), so validation is unaffected — only the row is dropped.
   */
  private readEntry(thread: Thread, addr: Address, silent = false) {
    const txn = thread.currentTxn;
    const entry = txn
      ? txn.readEntry(addr)
      : this.store.read(addr, this.store.committedVersion);
    const version = entry ? entry.begin : txn ? txn.readVersion : this.store.committedVersion;
    if (!silent) {
      this.emit({
        type: "read",
        time: thread.time,
        thread: thread.id,
        txn: txn ? txn.id : "auto",
        addr,
        version,
        value: entry ? renderStored(entry.value!) : "absent",
        line: thread.currentLine,
      }, thread);
    }
    return entry;
  }

  private readStruct(
    thread: Thread,
    addr: Address,
    span: ast.Span,
    silent = false
  ): { typeName: string; fields: Record<string, Value> } {
    const entry = this.readEntry(thread, addr, silent);
    if (!entry || entry.value?.kind !== "struct") {
      throw new RuntimeError(`expected an object at ${addr}`, span);
    }
    return entry.value;
  }

  private writeObject(thread: Thread, addr: Address, obj: StoredObject): void {
    this.writeBatch(thread, [[addr, obj]]);
  }

  private writeBatch(thread: Thread, writes: Array<[Address, StoredObject | null]>): void {
    const txn = thread.currentTxn;
    let version = 0;
    if (txn) {
      for (const [addr, obj] of writes) txn.write(addr, obj);
    } else {
      version = this.store.commit(new Map(writes));
    }
    for (const [addr, obj] of writes) {
      const at = this.emit({
        type: "write",
        time: thread.time,
        thread: thread.id,
        txn: txn ? txn.id : "auto",
        addr,
        value: obj === null ? "<deleted>" : renderStored(obj),
        line: thread.currentLine,
      }, thread);
      // A non-transactional write is its own single-object commit, so the write
      // row itself is what a later read of that version reads from. (Inside a
      // txn nothing is published until commit, which records authorship there.)
      if (!txn) this.causal?.noteCommit([addr], version, at);
    }
  }

  private liveChildren(thread: Thread, setAddr: Address): Address[] {
    const txn = thread.currentTxn;
    return txn ? txn.liveChildren(setAddr) : this.store.liveChildren(setAddr, this.store.committedVersion);
  }

  // -- builtins -----------------------------------------------------------

  /** The host-environment snapshot, or a runtime error if there is no host
   *  process to read (the browser playground). Mirrors how file IO errors out
   *  there rather than failing silently. */
  private requireEnv(span: ast.Span): string[] {
    if (this.envSnapshot === null)
      throw new RuntimeError("environment variables are not available in the browser playground", span);
    return this.envSnapshot;
  }

  private rand(): number {
    // mulberry32 PRNG for deterministic runs.
    this.rngState = (this.rngState + 0x6d2b79f5) >>> 0;
    let t = this.rngState;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  // `print` and `str` are handled directly in `evalCall` (they're generators, so
  // a `Show` impl's `show` method can run); the rest are pure and live here.
  private builtins: Record<string, (args: Value[], thread: Thread, span: ast.Span) => Value> = {
    rand: () => float(this.rand()),
    randInt: (args, _t, span) => {
      const n = args[0];
      if (!n || !isNumeric(n)) throw new RuntimeError("randInt expects a number", span);
      return int(Math.floor(this.rand() * n.value));
    },
    // `now()` — the current wall-clock time as an ISO-8601 string, for
    // timestamping log records. It reads the clock (an `io` builtin), so it is
    // non-deterministic, unlike the seeded `rand`.
    now: () => str(new Date().toISOString()),
    // The process-environment seam (see `io.stm`): the command-line arguments the
    // CLI plumbed in (`opts.args`, sans program name) and the host env snapshot,
    // each as count + indexed accessor for `args()`/`envVars()` to assemble. The
    // accessors error out of range like `str.charAt`.
    _argCount: () => int(this.cliArgs.length),
    _argAt: (args, _t, span) => {
      const i = intArg(args, 0, "_argAt", span);
      if (i < 0 || i >= this.cliArgs.length)
        throw new RuntimeError(`_argAt: index ${i} out of range (${this.cliArgs.length} argument(s))`, span);
      return str(this.cliArgs[i]!);
    },
    _envCount: (_a, _t, span) => int(this.requireEnv(span).length),
    _envEntry: (args, _t, span) => {
      const env = this.requireEnv(span);
      const i = intArg(args, 0, "_envEntry", span);
      if (i < 0 || i >= env.length)
        throw new RuntimeError(`_envEntry: index ${i} out of range (${env.length} entries)`, span);
      return str(env[i]!);
    },
    abs: (args, _t, span) => numFn(Math.abs, args, span),
    sqrt: (args, _t, span) => float(Math.sqrt(numArg(args, span))),
    floor: (args, _t, span) => int(Math.floor(numArg(args, span))),
    // File IO. Each fallible op returns a `Res<IOError, T>` — `Ok(value)` or
    // `Err(IOError{message})` — rather than throwing, so a program handles a bad
    // path with a `match`. `node:fs` is present under the CLI/tests but absent in
    // the browser playground bundle (the build stubs it), so a call there throws
    // out of the native layer — file IO "errors out" in the playground.
    fileExists: (args, _t, span) => {
      const path = strArg(args, 0, "fileExists", span);
      try {
        statSync(path);
        return resOk(bool(true));
      } catch (e) {
        // ENOENT is a real "no" (the file, or a path component, is absent); any
        // other stat error (permissions, an unreadable filesystem, …) means we
        // couldn't tell, so surface it rather than reporting a false "absent".
        if ((e as NodeJS.ErrnoException).code === "ENOENT") return resOk(bool(false));
        return resErr((e as Error).message);
      }
    },
    // Handle-based file IO: `open` returns a `File` wrapping the OS file
    // descriptor (an int), and `read`/`write`/`close` are `builtin` methods on it
    // (keyed `File_method`, receiver first — see `invokeUserMethod`). node:fs's
    // synchronous calls fit the interpreter's single-threaded model directly.
    open: (args, _t, span) => {
      const path = strArg(args, 0, "open", span);
      const flag = fileModeFlag(args[1], span);
      try {
        return resOk(fileVal(openSync(path, flag)));
      } catch (e) {
        return resErr((e as Error).message);
      }
    },
    // The standard streams are `File` handles at the fixed OS fds 0/1/2 (a real
    // `open` returns 3+, so they never collide).
    stdin: () => fileVal(STDIN_FD),
    stdout: () => fileVal(STDOUT_FD),
    stderr: () => fileVal(STDERR_FD),
    File_read: (args, _t, span) => {
      const fd = fileHandle(args, "read", span);
      if (fd === STDOUT_FD || fd === STDERR_FD) return resErr("cannot read from stdout/stderr");
      // `read` on `stdin` (fd 0) reads until EOF — real, blocking IO, so a program
      // reading stdin is interactive under `stm run` (it steps out of virtual time).
      try {
        return resOk(str(readFileSync(fd, "utf8")));
      } catch (e) {
        return resErr((e as Error).message);
      }
    },
    File_readLine: (args, _t, span) => {
      const fd = fileHandle(args, "readLine", span);
      if (fd === STDOUT_FD || fd === STDERR_FD) return resErr("cannot read from stdout/stderr");
      try {
        const line = readLineSync(fd);
        return resOk(line === null ? optionNone() : optionSome(str(line)));
      } catch (e) {
        return resErr((e as Error).message);
      }
    },
    File_write: (args, thread, span) => {
      const fd = fileHandle(args, "write", span);
      const data = strArg(args, 1, "write", span);
      if (fd === STDIN_FD) return resErr("cannot write to stdin");
      // stdout/stderr are simulated as observable trace output (like `print`), so
      // a program's writes show up in the trace and the CLI echo.
      if (fd === STDOUT_FD || fd === STDERR_FD) {
        const message = data.replace(/\n$/, "");
        this.emit({ type: "print", time: thread.time, thread: thread.id, message, line: thread.currentLine }, thread);
        if (this.echo) console.log(`[T${thread.id} t=${thread.time.toFixed(2)}] ${message}`);
        return resOk(UNIT);
      }
      try {
        writeSync(fd, data);
        return resOk(UNIT);
      } catch (e) {
        return resErr((e as Error).message);
      }
    },
    File_close: (args, _t, span) => {
      const fd = fileHandle(args, "close", span);
      if (fd <= STDERR_FD) return resOk(UNIT); // std streams: no-op, keep them open
      try {
        closeSync(fd);
        return resOk(UNIT);
      } catch (e) {
        return resErr((e as Error).message);
      }
    },
    // TCP networking. Real sockets don't fit the interpreter's deterministic
    // virtual-time model — a blocking `accept`/`recv` has no meaning in simulated
    // time — so, like a table's `loadCsv`, net runs on the Go backend only; a call
    // here errors out, pointing at `stm go`. `listen`/`connect` are free functions;
    // the socket ops are `builtin` methods (`conn.send`, `listener.accept`, …),
    // plugged in under their `Type_method` key (see `invokeUserMethod`).
    listen: (_a, _t, span) => netOnlyGo("listen", span),
    connect: (_a, _t, span) => netOnlyGo("connect", span),
    TcpConn_send: (_a, _t, span) => netOnlyGo("send", span),
    TcpConn_recv: (_a, _t, span) => netOnlyGo("recv", span),
    TcpConn_close: (_a, _t, span) => netOnlyGo("close", span),
    TcpListener_accept: (_a, _t, span) => netOnlyGo("accept", span),
    TcpListener_close: (_a, _t, span) => netOnlyGo("close", span),

    // -- string & char primitives (the native part of `impl str`/`impl char`) --
    // Indexing is by Unicode code point (`[...s]` splits by code point), so a
    // string's length and `charAt` agree with `char` — one code point per index.
    str_len: (args, _t, span) => int([...strArg(args, 0, "len", span)].length),
    str_charAt: (args, _t, span) => {
      const cps = [...strArg(args, 0, "charAt", span)];
      const i = intArg(args, 1, "charAt", span);
      if (i < 0 || i >= cps.length) throw new RuntimeError(`charAt: index ${i} out of range (len ${cps.length})`, span);
      return char(cps[i]!.codePointAt(0)!);
    },
    // `slice(start, end)` is half-open and *clamped* to `[0, len]`, so it never
    // errors — handy for parsers computing indices.
    str_slice: (args, _t, span) => {
      const cps = [...strArg(args, 0, "slice", span)];
      const n = cps.length;
      const clamp = (x: number) => Math.max(0, Math.min(x, n));
      const start = clamp(intArg(args, 1, "slice", span));
      const end = Math.max(start, clamp(intArg(args, 2, "slice", span)));
      return str(cps.slice(start, end).join(""));
    },
    // First code-point index of char `c`, or -1.
    str_indexOf: (args, _t, span) => {
      const cps = [...strArg(args, 0, "indexOf", span)];
      const c = charArg(args, 1, "indexOf", span);
      return int(cps.findIndex((cp) => cp.codePointAt(0) === c));
    },
    char_code: (args, _t, span) => int(charArg(args, 0, "code", span)),

    // -- Eq for the scalars (the native part of the prelude's `impl Eq for …`) --
    // The prelude declares each scalar's `eq` `builtin` rather than writing it as
    // `self == other`, which would recurse through the very operator it defines.
    // All five plug in the same value equality the `==` operator lowers to, so
    // `1.eq(1)` and `1 == 1` are the same comparison reached two ways.
    int_eq: (args, _t, span) => scalarEq("int", args, span),
    float_eq: (args, _t, span) => scalarEq("float", args, span),
    char_eq: (args, _t, span) => scalarEq("char", args, span),
    str_eq: (args, _t, span) => scalarEq("str", args, span),
    bool_eq: (args, _t, span) => scalarEq("bool", args, span),
    // The containers' `Eq`, declared `builtin` in `collections.stm` for the same
    // reason. A container is a heap value, so `valuesEqual` compares it by address
    // — identity, the documented default for a heap type, and what `==` on a
    // container already meant. They share the scalars' implementation.
    List_eq: (args, _t, span) => scalarEq("List", args, span),
    Set_eq: (args, _t, span) => scalarEq("Set", args, span),
    Dict_eq: (args, _t, span) => scalarEq("Dict", args, span),

    // -- Arithmetic for the scalars (the native part of the prelude's `impl Add /
    // Sub / Mul / Div for …`) -- Declared `builtin` there for the same reason as
    // `eq`: `self OP other` would recurse if the operator resolved through its trait
    // for scalars. Each plugs in the same arithmetic the operator lowers to, so
    // `6.mul(7)` and `6 * 7` are the same operation reached two ways. `Add for str`
    // is concatenation, matching `+` on strings; `-`/`*`/`/` are numeric only.
    int_add: (args, _t, span) => scalarArith("+", "int", args, span),
    float_add: (args, _t, span) => scalarArith("+", "float", args, span),
    str_add: (args, _t, span) => scalarArith("+", "str", args, span),
    int_sub: (args, _t, span) => scalarArith("-", "int", args, span),
    float_sub: (args, _t, span) => scalarArith("-", "float", args, span),
    int_mul: (args, _t, span) => scalarArith("*", "int", args, span),
    float_mul: (args, _t, span) => scalarArith("*", "float", args, span),
    int_div: (args, _t, span) => scalarArith("/", "int", args, span),
    float_div: (args, _t, span) => scalarArith("/", "float", args, span),
  };
}

/** A scalar's `Eq.eq`, shared by all five prelude impls: the same `valuesEqual`
 *  the `==` operator uses. Both operands are already the implementing type — the
 *  checker checked the argument against the impl's `Self` — so this only guards
 *  against a missing one. */
function scalarEq(type: string, args: Value[], span: ast.Span): Value {
  if (args.length !== 2) {
    throw new RuntimeError(`\`${type}.eq\` expects 1 argument, got ${args.length - 1}`, span);
  }
  return bool(valuesEqual(args[0]!, args[1]!));
}

/** A scalar's arithmetic trait method (`Add.add`/`Sub.sub`/`Mul.mul`/`Div.div`),
 *  the native part of `impl Add / Sub / Mul / Div for int/float` (plus `Add for
 *  str`): the same arithmetic the operator lowers to in `binaryOp`. Both operands
 *  are already the implementing type — the checker checked the argument against the
 *  impl's `Self` — so `+` on `str` concatenates and the numerics stay in their own
 *  type; this only guards against a missing or mistyped operand. */
function scalarArith(op: string, type: string, args: Value[], span: ast.Span): Value {
  const method = ARITH_METHOD[op]!;
  if (args.length !== 2) {
    throw new RuntimeError(`\`${type}.${method}\` expects 1 argument, got ${args.length - 1}`, span);
  }
  const a = args[0]!;
  const b = args[1]!;
  if (op === "+" && a.kind === "string" && b.kind === "string") return str(a.value + b.value);
  if (isNumeric(a) && isNumeric(b)) {
    const resultFloat = a.kind === "float" || b.kind === "float";
    const num = (n: number): Value => (resultFloat ? float(n) : int(n));
    switch (op) {
      case "+": return num(a.value + b.value);
      case "-": return num(a.value - b.value);
      case "*": return num(a.value * b.value);
      case "/":
        if (b.value === 0) throw new RuntimeError("division by zero", span);
        return resultFloat ? float(a.value / b.value) : int(Math.trunc(a.value / b.value));
    }
  }
  throw new RuntimeError(`\`${type}.${method}\` expects two ${type}s`, span);
}

/** Overloadable arithmetic operator -> the trait method it dispatches to, mirroring
 *  `OPERATOR_TRAITS` in the checker and `ARITH_METHOD` in the monomorphizer. */
const ARITH_METHOD: Record<string, string> = { "+": "add", "-": "sub", "*": "mul", "/": "div" };

/** Render an error nothing caught, the way an aborting program reports it: the
 *  error's type and message, the stack captured where it was raised, and then each
 *  `raise … from …` cause with its own stack (see docs/exceptions.md). A raise on a
 *  spawned thread names that thread, since its stack does not reach `main`. */
function formatUncaught(err: RaisedError, thread: Thread): string {
  const where = thread.id === 0 ? "" : ` (on thread ${thread.id}, spawned as \`${thread.fnName}\`)`;
  const lines: string[] = [];
  let e: RaisedError | null = err;
  while (e) {
    const label = lines.length === 0 ? `error${where}` : "caused by";
    lines.push(`${label}: ${e.typeName ?? "error"}: ${e.message}`);
    for (const f of e.stack) lines.push(`  at ${f.fn} (${f.source ? `${f.source}:` : "line "}${f.line})`);
    e = e.cause;
  }
  return lines.join("\n");
}

/** The interpreter has no TCP: its scheduler is a deterministic virtual-time
 *  simulator, where a blocking socket op can't be expressed. Net is a Go-backend
 *  feature (mirroring how `loadCsv` is), so a net builtin here throws, pointing at
 *  `stm go`. */
function netOnlyGo(name: string, span: ast.Span): never {
  throw new RuntimeError(
    `\`${name}\` (TCP networking) is only supported by the Go backend; compile with \`stm go\``,
    span
  );
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

/** The built-in method names per container — these always win over a user `impl`
 *  method of the same name (so a program can't shadow `size`/`add`/…). Mirrors the
 *  methods dispatched in `setMethod`/`dictMethod`/`listMethod`. */
const CONTAINER_BUILTIN_METHODS: Record<string, Set<string>> = {
  Set: new Set(["add", "remove", "contains", "size"]),
  Dict: new Set(["set", "get", "has", "contains", "remove", "size"]),
  List: new Set(["append", "get", "set", "size"]),
};

function isBuiltinContainerMethod(container: string, method: string): boolean {
  return CONTAINER_BUILTIN_METHODS[container]?.has(method) ?? false;
}

/** The native cursor type each iterable container's `iter()` hands back (its STM
 *  `Iterator` type from `collections.stm`). Keyed by container name. */
const CONTAINER_ITER_CURSORS: Record<string, string> = {
  Set: "SetIter",
  Dict: "DictIter",
  List: "ListIter",
};

/** A collection type-argument's display name (stored on the collection marker for
 *  traces). A function type has no single name, so it renders as `fn`. */
function typeArgName(t: ast.TypeExpr): string {
  return t.kind === "named" ? t.name : "fn";
}

/** The same display name, from an *inferred* type — a list literal writes no type
 *  argument, so its element type comes from the checker. Head name only, matching
 *  `typeArgName` (a `List<Dict<…>>` displays its element as `Dict`). */
function typeArgDisplay(t: Type): string {
  switch (t.k) {
    case "struct":
    case "struct-generic":
    case "enum":
    case "enum-generic":
    case "trait":
    case "generic":
    case "var":
      return t.name;
    case "fn":
      return "fn";
    default:
      return t.k;
  }
}

/** Overloadable operator -> the `impl` method it dispatches to and how the result
 *  is interpreted. Arithmetic returns a value; `eq` a bool (negated for `!=`);
 *  `cmp` a number compared against 0. Mirrors `OPERATOR_TRAITS` in the checker. */
const OPERATOR_DISPATCH: Record<string, { method: string; kind: "arith" | "eq" | "ord" }> = {
  "+": { method: "add", kind: "arith" },
  "-": { method: "sub", kind: "arith" },
  "*": { method: "mul", kind: "arith" },
  "/": { method: "div", kind: "arith" },
  "==": { method: "eq", kind: "eq" },
  "!=": { method: "eq", kind: "eq" },
  "<": { method: "cmp", kind: "ord" },
  ">": { method: "cmp", kind: "ord" },
  "<=": { method: "cmp", kind: "ord" },
  ">=": { method: "cmp", kind: "ord" },
};

/** Interpret an `Ord.cmp` result (negative/zero/positive) for a comparison op. */
function compareToZero(op: string, n: number): boolean {
  switch (op) {
    case "<":
      return n < 0;
    case ">":
      return n > 0;
    case "<=":
      return n <= 0;
    default:
      return n >= 0; // ">="
  }
}

function renderStored(obj: StoredObject): string {
  switch (obj.kind) {
    case "struct": {
      const fields = Object.entries(obj.fields)
        .map(([k, v]) => `${k}: ${displayValue(v)}`)
        .join(", ");
      return `${obj.typeName}{${fields}}`;
    }
    case "collection":
      return `${obj.container}<${obj.typeArgs.join(", ")}>`;
    case "entry":
      // A Set entry (value VOID) shows as membership; a Dict/List entry as a map.
      return obj.value.kind === "void"
        ? `∈ ${displayValue(obj.key)}`
        : `${displayValue(obj.key)} → ${displayValue(obj.value)}`;
    case "keyset":
      return "keyset↑";
  }
}

function numArg(args: Value[], span: ast.Span): number {
  const a = args[0];
  if (!a || !isNumeric(a)) throw new RuntimeError("expected a number", span);
  return a.value;
}

/** A string argument by position, for a native builtin. */
function strArg(args: Value[], i: number, fnName: string, span: ast.Span): string {
  const a = args[i];
  if (!a || a.kind !== "string") throw new RuntimeError(`${fnName} expects a string argument`, span);
  return a.value;
}

/** An int argument by position, for a native builtin. */
function intArg(args: Value[], i: number, fnName: string, span: ast.Span): number {
  const a = args[i];
  if (!a || a.kind !== "int") throw new RuntimeError(`${fnName} expects an int argument`, span);
  return a.value;
}

/** A char argument by position (returns its code point), for a native builtin. */
function charArg(args: Value[], i: number, fnName: string, span: ast.Span): number {
  const a = args[i];
  if (!a || a.kind !== "char") throw new RuntimeError(`${fnName} expects a char argument`, span);
  return a.value;
}

/** The `Unit` value — an inline, field-less value type (see `isValueType`), the
 *  success payload of a call that has nothing to return but "it worked"
 *  (`file.write`, `conn.send`, …). */
const UNIT: Value = { kind: "record", typeName: "Unit", fields: {} };

/** `Res.Ok(value)` — the success side of a file-IO result. */
function resOk(value: Value): Value {
  return { kind: "enum", enumName: "Res", variant: "Ok", fields: { ok: value } };
}

/** `Res.Err(IOError{message})` — the failure side of a file-IO result. `IOError`
 *  is a value type, so it lives inline with no store allocation. */
function resErr(message: string): Value {
  return {
    kind: "enum",
    enumName: "Res",
    variant: "Err",
    fields: { err: { kind: "record", typeName: "IOError", fields: { message: str(message) } } },
  };
}

/** `Option.Some(value)` / `Option.None` — `readLine`'s success payload (`None` at
 *  end of input). */
function optionSome(value: Value): Value {
  return { kind: "enum", enumName: "Option", variant: "Some", fields: { value } };
}
function optionNone(): Value {
  return { kind: "enum", enumName: "Option", variant: "None", fields: {} };
}

/** Read one line (up to and not including `\n`, and dropping a trailing `\r`) from
 *  `fd`, or `null` at end of input. Byte-at-a-time via `readSync` so the fd's
 *  position is exact — no read-ahead — and multi-byte UTF-8 is decoded whole. On
 *  `stdin` this blocks until a line is available (interactive under `stm run`). */
function readLineSync(fd: number): string | null {
  const bytes: number[] = [];
  const one = Buffer.alloc(1);
  let readAny = false;
  while (true) {
    const n = readSync(fd, one, 0, 1, null);
    if (n === 0) break; // end of input
    readAny = true;
    if (one[0] === 0x0a) break; // '\n'
    bytes.push(one[0]!);
  }
  if (!readAny) return null; // nothing left — end of input
  let line = Buffer.from(bytes).toString("utf8");
  if (line.endsWith("\r")) line = line.slice(0, -1);
  return line;
}

/** The standard streams' fixed file descriptors — the `File` handles `stdin`/
 *  `stdout`/`stderr` carry. A real `open` returns 3+, so they never collide. */
const STDIN_FD = 0;
const STDOUT_FD = 1;
const STDERR_FD = 2;

/** A `File` value wrapping an OS file descriptor. Like `IOError`/`Unit` it is a
 *  value type (its one field is `key`), so it lives inline. */
function fileVal(fd: number): Value {
  return { kind: "record", typeName: "File", fields: { handle: int(fd) } };
}

/** The fd out of a `File` receiver (`args[0]` of a `File_*` method). */
function fileHandle(args: Value[], method: string, span: ast.Span): number {
  const self = args[0];
  if (self?.kind === "record" && self.typeName === "File" && self.fields.handle?.kind === "int") {
    return self.fields.handle.value;
  }
  throw new RuntimeError(`\`${method}\` expects a File`, span);
}

/** The node:fs open flag for a `FileMode` value (`open`'s second argument). */
function fileModeFlag(mode: Value | undefined, span: ast.Span): string {
  if (mode?.kind === "enum" && mode.enumName === "FileMode") {
    switch (mode.variant) {
      case "Read":
        return "r";
      case "Write":
        return "w";
      case "Append":
        return "a";
    }
  }
  throw new RuntimeError("`open` expects a FileMode (Read/Write/Append)", span);
}

function numFn(fn: (n: number) => number, args: Value[], span: ast.Span): Value {
  const a = args[0];
  if (!a || !isNumeric(a)) throw new RuntimeError("expected a number", span);
  const r = fn(a.value);
  return a.kind === "int" ? int(r) : float(r);
}

